import { useEffect, useMemo, useRef, useState, Fragment } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import AnalisisMulticanalHeader from '../../components/admin/AnalisisMulticanalHeader'
import FotosMLPhotoroom from '../../components/admin/FotosMLPhotoroom'
import {
  getProducto, getMovimientos, getUsoEnKits, getProveedores, ajustarStock, registrarCompra,
  actualizarProducto, editarProducto, borrarProducto, restaurarProducto,
  getKitsQueUsan, pausarProducto,
  getKitsDisponibles, upsertProductoEnKit, quitarProductoDeKit,
  getGruposEquivalencia, getMiembrosGrupo, setGrupoEquivalencia
} from '../../lib/stockApi'
import { supabase } from '../../lib/supabase'
import { CATEGORIAS_ADMIN } from '../../lib/categoriasProducto'

// Fuente única de verdad en src/lib/categoriasProducto.js
const CATEGORIAS = CATEGORIAS_ADMIN
const TIERS = [
  { v: 'A', label: 'A · Premium' },
  { v: 'B', label: 'B · Calidad/precio' },
  { v: 'C', label: 'C · Marca masiva' },
  { v: 'D', label: 'D · Especialidades' },
]

// Helpers IVA: convertir entre precio s/IVA y c/IVA (los toSinIva/toConIva quedaron sin uso post-refactor).
function ivaFactor(pct) { return 1 + Number(pct || 22) / 100 }
function fmtMonto(n) {
  if (n == null || isNaN(n)) return '—'
  return '$' + Number(n).toLocaleString('es-UY', { maximumFractionDigits: 2 })
}

export default function Producto() {
  const { sku } = useParams()
  const navigate = useNavigate()
  const [producto, setProducto] = useState(null)
  const [stockModo, setStockModo] = useState('compra')
  const [compraCant, setCompraCant] = useState('')
  const [compraCosto, setCompraCosto] = useState('')
  const [ajusteCant, setAjusteCant] = useState('')
  const [ajusteMotivo, setAjusteMotivo] = useState('')
  const [guardandoStock, setGuardandoStock] = useState(false)
  const [stockMsg, setStockMsg] = useState(null)
  const [movs, setMovs] = useState([])
  const [usoKits, setUsoKits] = useState([])
  const [proveedores, setProveedores] = useState([])
  const [demanda, setDemanda] = useState({ d7: null, d14: null, d30: null })
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editingFicha, setEditingFicha] = useState(false)
  const [pausarModal, setPausarModal] = useState(false)
  const [borrando, setBorrando] = useState(false)
  const [kitsDisponibles, setKitsDisponibles] = useState([])
  const [kitModal, setKitModal] = useState(null)  // null | { mode: 'add'|'edit', kitCodigo?, cantidad }
  const [gruposExistentes, setGruposExistentes] = useState([])
  const [miembrosGrupo, setMiembrosGrupo] = useState([])
  const [similaresSinMedidas, setSimilaresSinMedidas] = useState([])
  const [copiandoSimilares, setCopiandoSimilares] = useState(false)
  const [mlPub, setMlPub] = useState(null)        // publicación ML activa o draft
  const [mlConfig, setMlConfig] = useState(null)  // config ML para cálculo neto
  const [bolsaInfo, setBolsaInfo] = useState(null)
  const [editandoGrupo, setEditandoGrupo] = useState(false)
  const [grupoInput, setGrupoInput] = useState('')
  const [activeTab, setActiveTab] = useState(null)  // null = usar default adaptativo
  const [mlDraft, setMlDraft] = useState({})        // cambios pendientes de sincronizar a ML
  const [mlSyncing, setMlSyncing] = useState(false)

  useEffect(() => { load() }, [sku])

  // Default adaptativo del tab según el canal donde está activo el producto.
  // Solo ML (publicado=false + publicar_en_ml=true o mlPub activo sin publicado) → ML
  // En tienda → Limpack · Sin publicar en ningún lado → General
  const tabDefault = useMemo(() => {
    if (!producto) return 'general'
    const enTienda = !!producto.publicado
    const enML = !!(mlPub && (mlPub.status === 'active' || mlPub.status === 'paused'))
    if (!enTienda && enML) return 'ml'        // exclusivo ML
    if (enTienda) return 'limpack'             // tiene tienda
    if (mlPub) return 'ml'                     // draft ML
    return 'general'
  }, [producto?.publicado, mlPub])
  const tab = activeTab ?? tabDefault

  // Estado de cada canal — para badges del header y empty states
  const canalEstado = useMemo(() => {
    if (!producto) return { tienda: 'oculto', ml: 'sin' }
    const tienda = producto.deleted_at ? 'eliminado'
      : producto.pausado ? 'pausado'
      : producto.publicado ? 'visible'
      : 'oculto'
    const ml = !mlPub ? 'sin'
      : mlPub.status === 'active' ? 'activo'
      : mlPub.status === 'paused' ? 'pausado'
      : mlPub.status === 'closed' ? 'cerrado'
      : 'draft'
    return { tienda, ml }
  }, [producto?.publicado, producto?.pausado, producto?.deleted_at, mlPub?.status])

  const esSoloML = canalEstado.tienda !== 'visible' && (canalEstado.ml === 'activo' || canalEstado.ml === 'pausado')
  const mlCambiosPendientes = Object.keys(mlDraft).length

  // Cargar publicación ML del producto + config + bolsa
  useEffect(() => {
    if (!producto?.id) return
    supabase.from('ml_publicaciones')
      .select('*')
      .eq('producto_id', producto.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setMlPub(data))
    supabase.from('app_config')
      .select('key, value')
      .in('key', ['ml_comision_pct', 'ml_costo_fijo_rangos'])
      .then(({ data }) => {
        const cfg = {}
        for (const r of data || []) {
          const v = r.value
          cfg[r.key] = typeof v === 'string' ? JSON.parse(v) : v
        }
        setMlConfig({
          comision_pct: Number(cfg.ml_comision_pct ?? 13.5),
          rangos: Array.isArray(cfg.ml_costo_fijo_rangos) ? cfg.ml_costo_fijo_rangos : [],
        })
      })
    if (producto.bolsa_recomendada_id) {
      supabase.from('v_bolsas_envio').select('sku, peso_vacia_g, largo_cm, ancho_cm, capacidad_max_kg, capacidad_max_litros')
        .eq('id', producto.bolsa_recomendada_id).maybeSingle()
        .then(({ data }) => setBolsaInfo(data))
    } else { setBolsaInfo(null) }
  }, [producto?.id, producto?.bolsa_recomendada_id])

  // Buscar hermanos (mismo marca + categoría + presentación) sin medidas — para banner "aplicar a hermanos"
  useEffect(() => {
    if (!producto?.marca || !producto?.categoria || !producto?.presentacion) { setSimilaresSinMedidas([]); return }
    supabase.from('productos')
      .select('id, sku, nombre')
      .eq('marca', producto.marca)
      .eq('categoria', producto.categoria)
      .eq('presentacion', producto.presentacion)
      .eq('activo', true)
      .is('deleted_at', null)
      .neq('id', producto.id)
      .or('peso_g.is.null,largo_cm.is.null,ancho_cm.is.null,alto_cm.is.null')
      .then(({ data }) => setSimilaresSinMedidas(data || []))
  }, [producto?.id, producto?.marca, producto?.categoria, producto?.presentacion, producto?.peso_g])

  async function aplicarASimilares() {
    if (!producto || similaresSinMedidas.length === 0) return
    const nombres = similaresSinMedidas.slice(0, 5).map(x => '• ' + x.nombre).join('\n')
    const extra = similaresSinMedidas.length > 5 ? `\n... y ${similaresSinMedidas.length - 5} más` : ''
    if (!confirm(`Copiar medidas (peso ${producto.peso_g}g + ${producto.largo_cm}×${producto.ancho_cm}×${producto.alto_cm} cm + bolsa) a ${similaresSinMedidas.length} producto${similaresSinMedidas.length > 1 ? 's' : ''} hermano${similaresSinMedidas.length > 1 ? 's' : ''}?\n\n${nombres}${extra}`)) return
    setCopiandoSimilares(true)
    try {
      for (const sim of similaresSinMedidas) {
        const { error } = await supabase.rpc('admin_actualizar_datos_fisicos', {
          p_id: sim.id,
          p_peso_g: producto.peso_g,
          p_largo_cm: producto.largo_cm,
          p_ancho_cm: producto.ancho_cm,
          p_alto_cm: producto.alto_cm,
          p_bolsa_recomendada_id: producto.bolsa_recomendada_id,
        })
        if (error) throw error
      }
      alert(`✓ Medidas copiadas a ${similaresSinMedidas.length} producto${similaresSinMedidas.length > 1 ? 's' : ''}`)
      setSimilaresSinMedidas([])
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setCopiandoSimilares(false)
    }
  }

  async function guardarStock() {
    if (!producto?.id) return
    setGuardandoStock(true); setStockMsg(null)
    try {
      if (stockModo === 'compra') {
        const cant = Math.round(Number(compraCant))
        const costo = Number(compraCosto)
        if (!cant || cant <= 0) { setStockMsg('\u2715 Pone una cantidad'); setGuardandoStock(false); return }
        if (!costo || costo <= 0) { setStockMsg('\u2715 Pone el costo unitario'); setGuardandoStock(false); return }
        await registrarCompra({ productoId: producto.id, cantidad: cant, costoUnitario: costo })
        setCompraCant(''); setCompraCosto('')
        setStockMsg('\u2713 Compra registrada (+' + cant + ' u.) - ML se sincroniza solo')
      } else {
        const nueva = Math.round(Number(ajusteCant))
        if (isNaN(nueva) || nueva < 0) { setStockMsg('\u2715 Conta una cantidad valida'); setGuardandoStock(false); return }
        await ajustarStock({ productoId: producto.id, cantidadNueva: nueva, motivo: ajusteMotivo || 'Conteo/ajuste desde ficha' })
        setAjusteCant(''); setAjusteMotivo('')
        setStockMsg('\u2713 Stock ajustado a ' + nueva + ' - ML se sincroniza solo')
      }
      await load()
    } catch (e) {
      setStockMsg('\u2715 ' + (e.message || 'No se pudo'))
    } finally { setGuardandoStock(false) }
  }

  async function load() {
    setLoading(true)
    const p = await getProducto(sku)
    setProducto(p)
    if (p) {
      const [m, k, prov, kits, grupos, miembros, d7, d14, d30] = await Promise.all([
        getMovimientos(p.id, 100),
        getUsoEnKits(p.id),
        getProveedores(),
        getKitsDisponibles(),
        getGruposEquivalencia(),
        p.grupo_equivalencia ? getMiembrosGrupo(p.grupo_equivalencia) : Promise.resolve([]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 7 }).then(r => r.data?.[0]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 14 }).then(r => r.data?.[0]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 30 }).then(r => r.data?.[0]),
      ])
      setMovs(m); setUsoKits(k); setProveedores(prov); setKitsDisponibles(kits)
      setGruposExistentes(grupos); setMiembrosGrupo(miembros)
      setGrupoInput(p.grupo_equivalencia || '')
      setDemanda({ d7, d14, d30 })
    }
    setLoading(false)
  }

  async function handleSaveGrupo() {
    if (!producto) return
    const valor = (grupoInput || '').trim().toLowerCase().replace(/\s+/g, '_')
    try {
      await setGrupoEquivalencia(producto.id, valor || null)
      setEditandoGrupo(false)
      await load()
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
    }
  }

  async function handleQuitarGrupo() {
    if (!producto || !confirm('¿Sacar este producto del grupo de equivalencia?')) return
    try {
      await setGrupoEquivalencia(producto.id, null)
      setEditandoGrupo(false)
      await load()
    } catch (err) {
      alert('Error: ' + (err.message || err))
    }
  }

  async function handleSaveKitAssignment() {
    if (!kitModal || !producto) return
    const cantidad = Number(kitModal.cantidad)
    if (!kitModal.kitCodigo || !cantidad || cantidad <= 0) {
      alert('Elegí kit y cantidad mayor a 0')
      return
    }
    try {
      await upsertProductoEnKit(producto.id, kitModal.kitCodigo, cantidad)
      setKitModal(null)
      const k = await getUsoEnKits(producto.id)
      setUsoKits(k)
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
    }
  }

  async function handleRemoveFromKit(kitCodigo, kitNombre) {
    if (!confirm(`¿Quitar este producto del kit "${kitNombre}"?`)) return
    try {
      await quitarProductoDeKit(producto.id, kitCodigo)
      const k = await getUsoEnKits(producto.id)
      setUsoKits(k)
    } catch (err) {
      alert('Error al quitar: ' + (err.message || err))
    }
  }

  // Evolución de costo: tomamos las últimas N entradas
  const evolucionCosto = useMemo(() => {
    return movs
      .filter(m => m.tipo === 'entrada' && m.costo_unitario)
      .reverse()  // cronológico
      .map(m => ({
        fecha: new Date(m.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' }),
        costo: Number(m.costo_unitario),
        proveedor: m.proveedor?.nombre || '—'
      }))
  }, [movs])

  if (loading) return <AdminLayout><div className="loading">Cargando producto...</div></AdminLayout>
  if (!producto) return (
    <AdminLayout>
      <div className="page">
        <h1>Producto no encontrado</h1>
        <Link to="/admin/stock" className="btn ghost">← Volver a Stock</Link>
      </div>
    </AdminLayout>
  )

  const minMax = evolucionCosto.length > 1 ? {
    min: Math.min(...evolucionCosto.map(c => c.costo)),
    max: Math.max(...evolucionCosto.map(c => c.costo))
  } : null

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/stock" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a stock</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8 }}>
          <div>
            <h1 style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>{producto.nombre}</span>
              {esSoloML && (
                <span title="Solo se vende por MercadoLibre — no aparece en la tienda Limpack" style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 999,
                  background: '#FFF7ED', color: '#9A3412', border: '1px solid #FED7AA',
                  fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                }}>🟡 Solo ML</span>
              )}
              {canalEstado.tienda === 'visible' && !esSoloML && (
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 999,
                  background: 'rgba(16,185,129,0.1)', color: '#065F46',
                  fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }}></span>
                  En tienda
                </span>
              )}
              {canalEstado.ml === 'activo' && (
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 999,
                  background: 'rgba(59,130,246,0.1)', color: '#1E40AF',
                  fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B82F6' }}></span>
                  ML activo
                </span>
              )}
              {canalEstado.ml === 'pausado' && (
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 999,
                  background: '#FEF3C7', color: '#92400E',
                  fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                }}>ML pausado</span>
              )}
              {canalEstado.ml === 'draft' && (
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 999,
                  background: '#F1F5F9', color: '#475569',
                  fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                }}>ML draft</span>
              )}
            </h1>
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>
              <code style={{ background: 'var(--paper)', padding: '2px 8px', borderRadius: 4 }}>{producto.sku}</code>
              {producto.marca && <span> · {producto.marca}</span>}
              <span> · {producto.presentacion}</span>
              <span> · {producto.proveedor?.nombre || '— sin proveedor —'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {producto.deleted_at ? (
              <button
                className="btn primary"
                style={{ background: '#10B981', borderColor: '#10B981' }}
                onClick={async () => {
                  if (!confirm(`¿Restaurar ${producto.sku} — ${producto.nombre}?\nVolverá a aparecer en el listado y en la Tienda.`)) return
                  try { await restaurarProducto(producto.id); load() } catch (e) { alert(e.message) }
                }}
              >♻️ Restaurar</button>
            ) : producto.pausado ? (
              <button
                className="btn primary"
                style={{ background: '#10B981', borderColor: '#10B981' }}
                onClick={async () => {
                  if (!confirm('¿Reanudar este producto? Volverá a aparecer en la Tienda.')) return
                  try { await pausarProducto(producto.id, false); load() } catch (e) { alert(e.message) }
                }}
              >▶️ Reanudar</button>
            ) : (
              <button className="btn ghost" onClick={() => setPausarModal(true)} style={{ borderColor: '#F59E0B', color: '#92400E' }}>⏸️ Pausar</button>
            )}
            {!producto.deleted_at && !producto.pausado && (
              <button
                className="btn ghost"
                title={producto.publicado ? 'Visible en la tienda — click para ocultar' : 'Oculto en la tienda — click para publicar'}
                onClick={async () => {
                  try {
                    await actualizarProducto(producto.id, { publicado: !producto.publicado })
                    load()
                  } catch (e) { alert('Error: ' + e.message) }
                }}
                style={{
                  borderColor: producto.publicado ? '#10B981' : '#94A3B8',
                  color: producto.publicado ? '#065F46' : '#475569',
                  background: producto.publicado ? '#ECFDF5' : '#F1F5F9',
                  fontWeight: 700,
                }}
              >
                {producto.publicado ? '✅ En tienda' : '⬜ Oculto en tienda'}
              </button>
            )}
            <button className="btn ghost" onClick={() => setEditing(true)}>✏️ Editar producto</button>
            {!producto.deleted_at && (
              <button
                className="btn ghost"
                disabled={borrando}
                style={{ borderColor: '#EF4444', color: '#991B1B' }}
                onClick={async () => {
                  if (!confirm(
                    `¿Borrar ${producto.sku} — ${producto.nombre}?\n\n` +
                    `Si tiene compras, kits o movimientos previos, se marca como eliminado (podés restaurarlo).\n` +
                    `Si está limpio, se borra definitivamente.\n\n` +
                    `Esta acción no se puede deshacer en el caso de borrado físico.`
                  )) return
                  setBorrando(true)
                  try {
                    const res = await borrarProducto(producto.id)
                    if (res?.modo === 'hard_delete') {
                      alert(`✓ ${res.sku} borrado definitivamente.`)
                      navigate('/admin/stock')
                    } else {
                      alert(
                        `${res.sku} marcado como eliminado.\n` +
                        `Tiene ${res.movimientos} movimiento(s), ${res.kits} kit(s) y ${res.compras} compra(s) que impiden borrarlo del todo.\n` +
                        `Podés restaurarlo cuando quieras.`
                      )
                      load()
                    }
                  } catch (e) { alert('Error al borrar: ' + e.message) }
                  finally { setBorrando(false) }
                }}
              >🗑️ Borrar</button>
            )}
          </div>
        </div>

        {producto.deleted_at && (
          <div style={{
            marginTop: 14, padding: '12px 16px',
            background: '#FEE2E2', border: '2px solid #FCA5A5', borderRadius: 12,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>🗑️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#7F1D1D' }}>Producto eliminado</strong>
              <span style={{ color: '#991B1B' }}> — borrado el {new Date(producto.deleted_at).toLocaleDateString('es-UY')}</span>
              <div style={{ fontSize: 11, color: '#991B1B', marginTop: 2 }}>
                No aparece en el listado ni en la Tienda. Conserva el historial. Tocá <strong>♻️ Restaurar</strong> para revertirlo.
              </div>
            </div>
          </div>
        )}
        {!producto.deleted_at && producto.pausado && (
          <div style={{
            marginTop: 14, padding: '12px 16px',
            background: '#FEF3C7', border: '2px solid #FCD34D', borderRadius: 12,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>⏸️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#78350F' }}>Producto pausado</strong>
              {producto.pausado_motivo && <span style={{ color: '#92400E' }}> — {producto.pausado_motivo}</span>}
              <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                No aparece en la Tienda. Sigue en kits hasta reanudarlo o sustituirlo.
              </div>
            </div>
          </div>
        )}

        {/* Stats principales */}
        <div className="cards-grid">
          <div className="card">
            <h3>Stock actual</h3>
            <div className="stat" style={{ color: producto.stock_actual <= producto.stock_critico ? '#EF4444' : producto.stock_actual <= producto.stock_minimo ? '#F59E0B' : 'var(--navy-ink)' }}>
              {Number(producto.stock_actual).toLocaleString('es-UY')}
            </div>
            <div className="desc">Mín {producto.stock_minimo} · Crítico {producto.stock_critico}</div>
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                <button onClick={() => { setStockModo('compra'); setStockMsg(null) }} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px solid ' + (stockModo === 'compra' ? '#00A8B5' : '#CBD5E1'), background: stockModo === 'compra' ? '#00A8B5' : '#fff', color: stockModo === 'compra' ? '#fff' : '#475569' }}>Compra</button>
                <button onClick={() => { setStockModo('ajuste'); setStockMsg(null) }} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px solid ' + (stockModo === 'ajuste' ? '#00A8B5' : '#CBD5E1'), background: stockModo === 'ajuste' ? '#00A8B5' : '#fff', color: stockModo === 'ajuste' ? '#fff' : '#475569' }}>Conteo/ajuste</button>
              </div>
              {stockModo === 'compra' ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="number" inputMode="numeric" value={compraCant} onChange={e => setCompraCant(e.target.value)} placeholder="cantidad" style={{ width: 84, padding: '7px 9px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 14 }} />
                  <input type="number" inputMode="decimal" value={compraCosto} onChange={e => setCompraCosto(e.target.value)} placeholder="$ costo unit." style={{ width: 108, padding: '7px 9px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 14 }} />
                  <button onClick={guardarStock} disabled={guardandoStock || !Number(compraCant) || !Number(compraCosto)} style={{ padding: '7px 14px', background: '#00A8B5', color: '#fff', border: 0, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (guardandoStock || !Number(compraCant) || !Number(compraCosto)) ? 0.5 : 1 }}>{guardandoStock ? '\u2026' : 'Registrar'}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="number" inputMode="numeric" value={ajusteCant} onChange={e => setAjusteCant(e.target.value)} placeholder="contar en" style={{ width: 84, padding: '7px 9px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 14 }} />
                  <input value={ajusteMotivo} onChange={e => setAjusteMotivo(e.target.value)} placeholder="motivo (opcional)" style={{ width: 130, padding: '7px 9px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 13 }} />
                  <button onClick={guardarStock} disabled={guardandoStock || ajusteCant === ''} style={{ padding: '7px 14px', background: '#1A3461', color: '#fff', border: 0, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (guardandoStock || ajusteCant === '') ? 0.5 : 1 }}>{guardandoStock ? '\u2026' : 'Ajustar'}</button>
                </div>
              )}
              {stockMsg && <div style={{ fontSize: 11.5, marginTop: 8, fontWeight: 600, color: stockMsg[0] === '\u2713' ? '#059669' : '#DC2626' }}>{stockMsg}</div>}
            </div>
          </div>
          <div className="card">
            <h3>Costo unitario</h3>
            <div className="stat">${Number(producto.costo_unitario).toFixed(2)}</div>
            <div className="desc">c/IVA · s/IVA ${(Number(producto.costo_unitario) / (1 + Number(producto.iva_pct ?? 22)/100)).toFixed(2)} ({producto.iva_pct ?? 22}%)</div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid #5BBDD8' }}>
            <h3>Precio al público</h3>
            <div className="stat" style={{ color: '#5BBDD8' }}>
              {producto.precio_publico ? `$${Number(producto.precio_publico).toLocaleString('es-UY', { maximumFractionDigits: 0 })}` : '—'}
            </div>
            <div className="desc">{producto.precio_publico ? 'c/IVA · venta suelto' : 'Sin precio cargado'}</div>
          </div>
          {producto.precio_publico && Number(producto.precio_publico) > 0 && (() => {
            // Regla c/IVA: tanto costo como precio están c/IVA en DB. Margen directo.
            const costoCIva = Number(producto.costo_unitario)
            const precioCIva = Number(producto.precio_publico)
            const margenAbs = precioCIva - costoCIva
            const margenPct = (margenAbs / precioCIva) * 100
            const color = margenPct > 50 ? '#059669' : margenPct > 30 ? '#D97706' : '#DC2626'
            return (
              <div className="card" style={{ borderLeft: `4px solid ${color}` }}>
                <h3>Margen unitario</h3>
                <div className="stat" style={{ color }}>{margenPct.toFixed(0)}%</div>
                <div className="desc">${margenAbs.toFixed(2)} por unidad · c/IVA</div>
              </div>
            )
          })()}
          <div className="card">
            <h3>Valor en stock</h3>
            <div className="stat">${(Number(producto.stock_actual) * Number(producto.costo_unitario)).toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
            <div className="desc">c/IVA · al costo</div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid var(--teal)' }}>
            <h3>Demanda 7 días</h3>
            <div className="stat" style={{ color: 'var(--teal)' }}>{Number(demanda.d7?.cantidad_total ?? 0).toFixed(0)}</div>
            <div className="desc">{demanda.d7?.cantidad_entregas ?? 0} entregas</div>
          </div>
          <div className="card">
            <h3>Demanda 30 días</h3>
            <div className="stat">{Number(demanda.d30?.cantidad_total ?? 0).toFixed(0)}</div>
            <div className="desc">{demanda.d30?.cantidad_entregas ?? 0} entregas</div>
          </div>
        </div>

        {/* Banner: copiar medidas a hermanos sin medidas */}
        {producto.peso_g && producto.largo_cm && producto.ancho_cm && producto.alto_cm && similaresSinMedidas.length > 0 && (
          <div style={{
            marginTop: 24,
            padding: '14px 18px',
            background: 'linear-gradient(135deg, rgba(0,168,181,0.08), rgba(26,52,97,0.04))',
            border: '1.5px solid rgba(0,168,181,0.4)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <div style={{ fontSize: 28 }}>📋</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#1A3461', fontSize: 13 }}>Aplicar estas medidas a productos hermanos</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 3, lineHeight: 1.4 }}>
                Hay <b>{similaresSinMedidas.length} producto{similaresSinMedidas.length > 1 ? 's' : ''}</b> con misma marca + categoría + presentación que comparten envase y todavía no tienen medidas:&nbsp;
                <span style={{ color: '#1A3461' }}>{similaresSinMedidas.slice(0, 3).map(s => s.nombre.replace(/^.*\((.*)\)$/, '$1')).join(' · ')}{similaresSinMedidas.length > 3 ? ` · +${similaresSinMedidas.length - 3} más` : ''}</span>
              </div>
            </div>
            <button
              onClick={aplicarASimilares}
              disabled={copiandoSimilares}
              style={{
                background: '#00A8B5',
                color: '#fff',
                border: 'none',
                padding: '10px 18px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: copiandoSimilares ? 'wait' : 'pointer',
                opacity: copiandoSimilares ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {copiandoSimilares ? 'Copiando…' : `📋 Aplicar a ${similaresSinMedidas.length}`}
            </button>
          </div>
        )}

        {/* ════ ANÁLISIS RENTABILIDAD MULTICANAL — sticky arriba de las tabs ════ */}
        <AnalisisMulticanalHeader productoId={producto?.id} />

        {/* ════ TABS POR CANAL — la ficha se reorganiza por contexto ════ */}
        <div style={{
          marginTop: 14,
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--line)',
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}>
          {[
            { id: 'general',  label: 'General',      icon: '📊' },
            { id: 'limpack',  label: 'Limpack',      icon: '💧' },
            { id: 'ml',       label: 'MercadoLibre', icon: '🟡', badge: mlCambiosPendientes > 0 ? mlCambiosPendientes : null },
            { id: 'envio',    label: 'Envío',        icon: '📐' },
            { id: 'b2b',      label: 'B2B',          icon: '🛒' },
          ].map(t => {
            const active = tab === t.id
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? '2px solid var(--navy-ink)' : '2px solid transparent',
                  padding: '12px 16px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? 'var(--navy-ink)' : 'var(--muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  position: 'relative',
                }}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
                {t.badge != null && (
                  <span style={{
                    background: '#3B82F6', color: '#fff',
                    fontSize: 10, padding: '1px 6px', borderRadius: 999,
                    fontWeight: 700, marginLeft: 2,
                  }}>{t.badge}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* ════ TAB: LIMPACK ════ */}
        {tab === 'limpack' && esSoloML && (
          <div style={{
            marginTop: 18, padding: 24,
            background: 'linear-gradient(180deg, #FFF7ED 0%, #fff 100%)',
            border: '1px solid #FED7AA', borderRadius: 12,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🟡</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, color: '#9A3412' }}>Este producto no se vende en tienda Limpack</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#7C2D12', lineHeight: 1.5 }}>
              Es exclusivo de MercadoLibre. No aparece en <strong>limpack.uy</strong>, no se incluye en kits y no se factura por la suscripción.<br />
              Si querés que también se venda en tienda, hacelo visible desde el botón "⬜ Oculto en tienda" arriba.
            </p>
            <button
              onClick={() => setActiveTab('ml')}
              style={{
                background: '#1A3461', color: '#fff', border: 'none',
                padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >Ir al tab MercadoLibre →</button>
          </div>
        )}
        {tab === 'limpack' && !esSoloML && (
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>

          {/* ── Encabezado del tab con CTAs ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>💧</span>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: '#1A3461', fontWeight: 800 }}>Cómo se ve en la tienda Limpack</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748B' }}>Preview de la ficha que ve el cliente en limpack.uy + datos del canal</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn ghost" onClick={() => setEditingFicha(true)} style={{ fontSize: 12, padding: '7px 12px' }}>
                {producto.descripcion ? '✏️ Editar ficha' : '+ Cargar ficha'}
              </button>
              <a href="https://limpack.uy/tienda" target="_blank" rel="noopener noreferrer" style={{
                fontSize: 12, padding: '7px 12px', borderRadius: 8,
                background: '#10B981', color: '#fff', textDecoration: 'none', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>↗ Ver en limpack.uy</a>
            </div>
          </div>

          {/* ── Preview de cómo se ve en la tienda ── */}
          <div style={{
            background: 'linear-gradient(180deg, #F8FAFC 0%, #fff 100%)',
            border: '1px solid #E2E8F0', borderRadius: 12, padding: 18,
            display: 'grid', gridTemplateColumns: '220px 1fr', gap: 22,
          }}>
            {/* Foto */}
            <div style={{
              background: '#F8FAFC', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 220, padding: 18,
              border: '1px solid #E2E8F0',
            }}>
              {producto.imagen_url
                ? <img src={producto.imagen_url} alt="" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                : <div style={{ textAlign: 'center', color: '#94A3B8' }}>
                    <div style={{ fontSize: 56, opacity: 0.4 }}>📦</div>
                    <div style={{ fontSize: 11, marginTop: 6 }}>Sin foto cargada</div>
                  </div>}
            </div>

            {/* Contenido tipo limpack.uy */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Nombre + tier badge */}
              <div style={{ marginBottom: 8 }}>
                <h2 style={{ margin: 0, fontSize: 22, color: '#1A3461', fontWeight: 800, lineHeight: 1.2 }}>{producto.nombre}</h2>
                <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>
                  {producto.marca && <span>{producto.marca} · </span>}{producto.presentacion}
                </div>
              </div>

              {/* Precio destacado */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#1A3461' }}>
                  ${Number(producto.precio_publico || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })}
                </span>
                <span style={{ fontSize: 12, color: '#64748B' }}>c/IVA · venta suelto</span>
                {producto.destacado && (
                  <span style={{ marginLeft: 8, fontSize: 11, padding: '3px 9px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase' }}>★ Destacado</span>
                )}
              </div>

              {/* Descripción */}
              {producto.descripcion ? (
                <p style={{ margin: '0 0 12px', color: '#1A3461', fontSize: 14, lineHeight: 1.55 }}>{producto.descripcion}</p>
              ) : (
                <p style={{ margin: '0 0 12px', color: '#94A3B8', fontSize: 13, fontStyle: 'italic' }}>
                  Sin descripción cargada. El cliente solo ve nombre + marca + presentación.
                  <button onClick={() => setEditingFicha(true)} style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#10B981', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>Cargar ahora →</button>
                </p>
              )}

              {/* Badges: aroma, rendimiento, ideal */}
              {(producto.aroma || producto.rendimiento || producto.ideal_para) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {producto.aroma && <span style={{ background: '#F1F5F9', padding: '4px 10px', borderRadius: 999, fontSize: 12, color: '#475569', fontWeight: 600 }}>🌸 {producto.aroma}</span>}
                  {producto.rendimiento && <span style={{ background: '#F1F5F9', padding: '4px 10px', borderRadius: 999, fontSize: 12, color: '#475569', fontWeight: 600 }}>📐 {producto.rendimiento}</span>}
                  {producto.ideal_para && <span style={{ background: '#F1F5F9', padding: '4px 10px', borderRadius: 999, fontSize: 12, color: '#475569', fontWeight: 600 }}>✓ {producto.ideal_para}</span>}
                </div>
              )}

              {/* Beneficios */}
              {producto.beneficios?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#1A3461', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>Beneficios</div>
                  <ul style={{ paddingLeft: 20, margin: 0, color: '#1A3461', fontSize: 13, lineHeight: 1.6 }}>
                    {producto.beneficios.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              {/* Modo de uso */}
              {producto.modo_uso && (
                <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
                  <strong style={{ color: '#1A3461' }}>Modo de uso:</strong> {producto.modo_uso}
                </div>
              )}

              {/* Composición */}
              {producto.composicion && (
                <div style={{ fontSize: 13, color: '#475569' }}>
                  <strong style={{ color: '#1A3461' }}>Composición:</strong> {producto.composicion}
                </div>
              )}
            </div>
          </div>

          {/* ── Datos del canal (lo que NO ve el cliente) ── */}
          <div style={{
            background: '#fff', border: '1px solid rgba(16,185,129,0.25)',
            borderLeft: '4px solid #10B981', borderRadius: 10, padding: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <strong style={{ fontSize: 13, color: '#1A3461' }}>📊 Datos del canal</strong>
              <span style={{
                fontSize: 10, padding: '3px 9px', borderRadius: 999,
                background: producto.publicado ? '#DCFCE7' : '#F1F5F9',
                color: producto.publicado ? '#065F46' : '#64748B',
                fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
              }}>{producto.publicado ? '✓ Visible' : 'Oculto'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Precio cliente</div>
                <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>${Number(producto.precio_publico || 0).toLocaleString('es-UY')}</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>c/IVA</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Costo unitario</div>
                <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>${Number(producto.costo_unitario || 0).toFixed(0)}</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>c/IVA</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Margen</div>
                <div style={{ fontSize: 16, color: '#10B981', fontWeight: 800 }}>
                  {producto.precio_publico && producto.costo_unitario
                    ? Math.round(((producto.precio_publico - producto.costo_unitario) / producto.precio_publico) * 100) + '%'
                    : '—'}
                </div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>
                  {producto.precio_publico && producto.costo_unitario
                    ? '$' + Math.round(producto.precio_publico - producto.costo_unitario) + ' por u.'
                    : 'sin precio'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Stock disponible</div>
                <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>{Math.max(0, Number(producto.stock_actual || 0) - Number(producto.stock_reservado || 0))} u.</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>físico − reservado</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Vendible suelto</div>
                <div style={{ fontSize: 16, color: producto.vendible_suelto !== false ? '#10B981' : '#94A3B8', fontWeight: 800 }}>{producto.vendible_suelto !== false ? '✓ Sí' : 'Solo kits'}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Tier</div>
                <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>{producto.tier || '—'}</div>
              </div>
            </div>
          </div>

        </div>
        )}

        {/* ════ TAB: ML ════ */}
        {tab === 'ml' && (
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>

          {/* ── Encabezado del tab con CTAs ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🟡</span>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: '#1A3461', fontWeight: 800 }}>Cómo se ve en MercadoLibre</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748B' }}>
                  Preview de la publicación + math del canal + CRUD modo borrador
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {mlPub ? (
                <>
                  <Link to={`/admin/canales/mercadolibre/publicar/${producto.id}`} className="btn ghost" style={{ fontSize: 12, padding: '7px 12px', textDecoration: 'none', color: '#475569', border: '1px solid #CBD5E1', borderRadius: 8 }}>
                    ✏️ Wizard completo
                  </Link>
                  {mlPub.permalink && (
                    <a href={mlPub.permalink} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: 12, padding: '7px 12px', borderRadius: 8,
                      background: '#3B82F6', color: '#fff', textDecoration: 'none', fontWeight: 700,
                    }}>↗ Ver en ML</a>
                  )}
                </>
              ) : (
                <Link to={`/admin/canales/mercadolibre/publicar/${producto.id}`} style={{
                  fontSize: 12, padding: '7px 14px', borderRadius: 8,
                  background: '#3B82F6', color: '#fff', textDecoration: 'none', fontWeight: 700,
                }}>+ Publicar en ML</Link>
              )}
            </div>
          </div>

          {/* ── Fotos MELI con Photoroom (3 variantes + swap tienda) ── */}
          <FotosMLPhotoroom
            productoId={producto.id}
            productoSku={producto.sku}
            onFotoTiendaSwap={() => load()}
          />

          {!mlPub && (
            <div style={{
              padding: 28, background: '#F8FAFC', border: '1px dashed #CBD5E1',
              borderRadius: 12, textAlign: 'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🟡</div>
              <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1A3461' }}>Sin publicación en MercadoLibre</h3>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748B' }}>
                Este producto todavía no tiene una publicación en ML. Abrí el wizard para crearla en 7 pasos.
              </p>
              <Link to={`/admin/canales/mercadolibre/publicar/${producto.id}`} style={{
                background: '#3B82F6', color: '#fff', padding: '10px 18px',
                borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 13,
              }}>+ Publicar en MercadoLibre</Link>
            </div>
          )}

          {mlPub && (() => {
            const precioML = Number(mlPub.precio || 0)
            const cm = Number(mlConfig?.comision_pct ?? 13.5)
            const rango = (mlConfig?.rangos || []).slice().sort((a, b) => a.hasta - b.hasta).find(r => precioML < r.hasta)
            const comision = Math.round(precioML * cm / 100)
            const costoFijo = rango ? rango.fijo : 0
            const neto = Math.round(precioML - comision - costoFijo)
            const dif = neto - Number(producto.precio_publico || 0)
            // Fotos: las leemos de ml_response_raw.pictures si están, sino caemos a la foto Limpack
            const fotosML = Array.isArray(mlPub.ml_response_raw?.pictures)
              ? mlPub.ml_response_raw.pictures.map(p => p.secure_url || p.url).filter(Boolean)
              : (producto.imagen_url ? [producto.imagen_url] : [])
            const atributos = Array.isArray(mlPub.attributes) ? mlPub.attributes : []
            return (
              <>
                {/* ── Preview tipo MercadoLibre ── */}
                <div style={{
                  background: '#fff', border: '1px solid #E2E8F0',
                  borderRadius: 12, padding: 18,
                  display: 'grid', gridTemplateColumns: '280px 1fr', gap: 22,
                }}>
                  {/* Galería 12 fotos */}
                  <div>
                    <div style={{
                      background: '#F8FAFC', borderRadius: 12,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      minHeight: 240, padding: 16, border: '1px solid #E2E8F0',
                    }}>
                      {fotosML.length > 0
                        ? <img src={fotosML[0]} alt="" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                        : <div style={{ textAlign: 'center', color: '#94A3B8' }}>
                            <div style={{ fontSize: 56, opacity: 0.4 }}>📷</div>
                            <div style={{ fontSize: 11, marginTop: 6 }}>Sin fotos cargadas</div>
                          </div>}
                    </div>
                    {/* Strip de 12 slots */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginTop: 8 }}>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} style={{
                          aspectRatio: '1', borderRadius: 4,
                          background: fotosML[i] ? `url(${fotosML[i]}) center/contain no-repeat #F8FAFC` : '#F1F5F9',
                          border: '1px solid #E2E8F0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, color: '#CBD5E1',
                        }}>
                          {!fotosML[i] && (i + 1)}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 10, color: '#94A3B8', textAlign: 'center', marginTop: 6 }}>
                      {fotosML.length} / 12 fotos
                    </div>
                  </div>

                  {/* Contenido tipo ML */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Status */}
                    <div style={{ marginBottom: 6 }}>
                      <span style={{
                        fontSize: 10, padding: '3px 9px', borderRadius: 999,
                        background: mlPub.status === 'active' ? '#DBEAFE' : mlPub.status === 'paused' ? '#FEF3C7' : '#F1F5F9',
                        color: mlPub.status === 'active' ? '#1E40AF' : mlPub.status === 'paused' ? '#92400E' : '#64748B',
                        fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
                      }}>{mlPub.status === 'active' ? '✓ Activa' : mlPub.status}</span>
                      {mlPub.is_catalog_listing && (
                        <span style={{ marginLeft: 6, fontSize: 10, padding: '3px 9px', borderRadius: 999, background: '#F3E8FF', color: '#7C3AED', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase' }}>Catálogo</span>
                      )}
                    </div>

                    {/* Título 60 chars */}
                    <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#1A3461', fontWeight: 800, lineHeight: 1.25 }}>{mlPub.titulo || producto.nombre}</h2>

                    {/* Precio destacado tipo ML */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 32, fontWeight: 800, color: '#1A3461' }}>${precioML.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</span>
                      {producto.precio_publico > 0 && (
                        <span style={{ fontSize: 12, color: '#10B981', fontWeight: 700 }}>+{Math.round((precioML / (producto.precio_publico || 1) - 1) * 100)}% vs web</span>
                      )}
                    </div>

                    {/* Envío + stock */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      {mlPub.free_shipping && (
                        <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 700 }}>🚚 Envío gratis</span>
                      )}
                      <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: '#F1F5F9', color: '#475569', fontWeight: 600 }}>
                        Stock publicado: {mlPub.available_quantity ?? '—'}
                      </span>
                      {mlPub.listing_type && (
                        <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: '#F1F5F9', color: '#475569', fontWeight: 600 }}>
                          Listing: {mlPub.listing_type}
                        </span>
                      )}
                    </div>

                    {/* Descripción ML */}
                    {mlPub.descripcion ? (
                      <p style={{ margin: '0 0 12px', color: '#1A3461', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{mlPub.descripcion}</p>
                    ) : (
                      <p style={{ margin: '0 0 12px', color: '#94A3B8', fontSize: 13, fontStyle: 'italic' }}>
                        Sin descripción cargada en ML. Cargala desde el wizard completo.
                      </p>
                    )}

                    {/* Atributos catálogo */}
                    {atributos.length > 0 && (
                      <div style={{ marginTop: 'auto', paddingTop: 10, borderTop: '1px dashed #E2E8F0' }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#1A3461', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>Atributos catálogo ML</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12, color: '#475569' }}>
                          {atributos.slice(0, 8).map((a, i) => (
                            <Fragment key={i}>
                              <span style={{ color: '#94A3B8' }}>{a.name || a.id}</span>
                              <strong style={{ color: '#1A3461' }}>{a.value_name || a.value || '—'}</strong>
                            </Fragment>
                          ))}
                          {atributos.length > 8 && (
                            <span style={{ gridColumn: '1 / -1', fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>+{atributos.length - 8} más</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Math del canal ── */}
                <div style={{
                  background: '#fff', border: '1px solid rgba(59,130,246,0.25)',
                  borderLeft: '4px solid #3B82F6', borderRadius: 10, padding: 16,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <strong style={{ fontSize: 13, color: '#1A3461' }}>💰 Math del canal</strong>
                    <span style={{ fontSize: 11, color: '#64748B' }}>cuánto queda neto para Limpack</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Precio ML</div>
                      <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>${precioML.toLocaleString('es-UY')}</div>
                      <div style={{ fontSize: 10, color: '#94A3B8' }}>c/IVA</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Comisión {cm}%</div>
                      <div style={{ fontSize: 16, color: '#DC2626', fontWeight: 800 }}>−${comision.toLocaleString('es-UY')}</div>
                    </div>
                    {costoFijo > 0 && (
                      <div>
                        <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Costo fijo</div>
                        <div style={{ fontSize: 16, color: '#DC2626', fontWeight: 800 }}>−${costoFijo}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>(&lt;${rango.hasta})</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Neto Limpack</div>
                      <div style={{ fontSize: 18, color: '#1A3461', fontWeight: 800 }}>${neto.toLocaleString('es-UY')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>vs precio web</div>
                      <div style={{ fontSize: 16, color: dif >= 0 ? '#10B981' : '#DC2626', fontWeight: 800 }}>{dif >= 0 ? '+' : ''}${dif.toLocaleString('es-UY')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Costo unitario</div>
                      <div style={{ fontSize: 16, color: '#1A3461', fontWeight: 800 }}>${Number(producto.costo_unitario || 0).toFixed(0)}</div>
                      <div style={{ fontSize: 10, color: dif - producto.costo_unitario >= 0 ? '#10B981' : '#DC2626' }}>
                        margen ML: {Math.round((neto - producto.costo_unitario) / neto * 100)}%
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )
          })()}

          {/* ─── CRUD ML modo borrador (placeholder por ahora) ─── */}
          {mlPub && (
            <MlBorradorPanel
              producto={producto}
              mlPub={mlPub}
              mlConfig={mlConfig}
              draft={mlDraft}
              setDraft={setMlDraft}
              syncing={mlSyncing}
              onSync={async () => {
                if (mlCambiosPendientes === 0) return
                if (!confirm(`Sincronizar ${mlCambiosPendientes} cambio${mlCambiosPendientes > 1 ? 's' : ''} a MercadoLibre?`)) return
                setMlSyncing(true)
                try {
                  // Mandamos sólo la parte editable de la descripción —
                  // la edge function se encarga de concatenar los bloques fijos
                  // leyendo app_config.ml_descripcion_fija (server-side).
                  const cambiosFinales = { ...mlDraft }
                  const { data: { session } } = await supabase.auth.getSession()
                  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-update-publicacion`
                  const resp = await fetch(url, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${session?.access_token || ''}`,
                      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      ml_publicacion_id: mlPub.id,
                      cambios: cambiosFinales,
                    }),
                  })
                  const data = await resp.json().catch(() => ({}))
                  if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
                  if (data.errores && data.errores.length > 0) {
                    alert(`⚠ Algunos cambios fallaron:\n${data.errores.map(e => `· ${e.campo}: ${e.error}`).join('\n')}`)
                  } else if (data.modo_shadow) {
                    alert(`📝 ${data.mensaje}\n\nMotivo: ${data.motivo}\n\nCuando se conecte la cuenta de ML, los cambios se empujan automáticamente.`)
                  } else {
                    alert('✓ Cambios sincronizados a MercadoLibre')
                  }
                  setMlDraft({})
                  load()
                } catch (e) {
                  alert('Error al sincronizar: ' + e.message)
                } finally {
                  setMlSyncing(false)
                }
              }}
              onDiscard={() => setMlDraft({})}
            />
          )}
        </div>
        )}

        {/* ════ TAB: ENVÍO ════ */}
        {tab === 'envio' && (
        <div style={{ marginTop: 18 }}>
          {/* ─── Card: Logística envío ─── */}
          <div style={{ background: '#fff', border: '1px solid rgba(0,168,181,0.25)', borderLeft: '4px solid #00A8B5', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>📐</span>
              <strong style={{ fontSize: 13, color: '#1A3461' }}>Logística · envío</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 12, color: '#475569' }}>
              <span>Peso (envase lleno)</span><strong style={{ color: producto.peso_g ? '#1A3461' : '#EF4444' }}>{producto.peso_g ? `${producto.peso_g} g` : '✗ Falta'}</strong>
              <span>Dimensiones</span><strong style={{ color: producto.largo_cm ? '#1A3461' : '#EF4444' }}>
                {producto.largo_cm ? `${producto.largo_cm} × ${producto.ancho_cm} × ${producto.alto_cm} cm` : '✗ Falta'}
              </strong>
              {producto.peso_g && producto.largo_cm && (() => {
                const volL = (producto.largo_cm * producto.ancho_cm * producto.alto_cm) / 1000
                const volK = volL / 6
                const pesoML = Math.max(producto.peso_g / 1000, volK)
                return (
                  <>
                    <span>Volumen</span><strong>{volL.toFixed(2)} L</strong>
                    <span>Peso volumétrico ML</span><strong>{volK.toFixed(2)} kg</strong>
                    <span>ML cobra por</span><strong style={{ color: pesoML === producto.peso_g / 1000 ? '#10B981' : '#B45309' }}>
                      {pesoML.toFixed(2)} kg ({pesoML === producto.peso_g / 1000 ? 'real' : 'volumen ⚠'})
                    </strong>
                  </>
                )
              })()}
              {bolsaInfo && (
                <>
                  <span>Bolsa</span><strong style={{ color: '#1A3461' }}>{bolsaInfo.sku === 'PACK_CHICO' ? 'Chica 50×60' : 'Grande 66×100'}</strong>
                  <span>Bolsa vacía</span><strong>{bolsaInfo.peso_vacia_g} g</strong>
                  <span>Capacidad bolsa</span><strong>{bolsaInfo.capacidad_max_kg} kg / {bolsaInfo.capacidad_max_litros} L</strong>
                </>
              )}
            </div>
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #E2E8F0', fontSize: 11 }}>
              <Link to="/admin/logistica/medidas-envio" style={{ color: '#00A8B5', textDecoration: 'none', fontWeight: 600 }}>↗ Ver todas las medidas</Link>
            </div>
          </div>

          {/* Simulador de envío real por barrio */}
          {producto.peso_g && producto.largo_cm && (
            <SimuladorEnvio producto={producto} />
          )}
        </div>
        )}

        {/* ════ TAB: B2B ════ */}
        {tab === 'b2b' && (
          <div style={{
            marginTop: 18, padding: 28,
            background: '#fff', border: '1px dashed var(--line)', borderRadius: 12,
            textAlign: 'center', color: 'var(--muted)',
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🛒</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1A3461' }}>Catálogo B2B</h3>
            <p style={{ margin: 0, fontSize: 13 }}>Acá va a vivir el precio empresas, descuentos por volumen y catálogo PDF. <em>En construcción.</em></p>
          </div>
        )}

        {/* (La ficha de tienda rica ya está integrada en el tab Limpack arriba) */}

        {/* ════ TAB: GENERAL — historia: costos, equivalencias, kits, movimientos ════ */}
        {tab === 'general' && (
        <>
        <h2 className="section-head" style={{ marginTop: 32 }}>📈 Evolución de costo</h2>
        {evolucionCosto.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)' }}>Todavía no se cargó ninguna compra. Cuando registres compras vas a ver acá la curva de precios.</div>
        ) : (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140, padding: '0 8px' }}>
              {evolucionCosto.map((c, i) => {
                const range = (minMax?.max ?? c.costo) - (minMax?.min ?? c.costo)
                const h = range > 0 ? ((c.costo - (minMax?.min ?? 0)) / range * 100 + 20) : 60
                const prevCosto = i > 0 ? evolucionCosto[i - 1].costo : c.costo
                const subio = c.costo > prevCosto
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: subio ? '#EF4444' : i > 0 ? '#10B981' : 'var(--navy-ink)' }}>
                      ${c.costo.toFixed(0)}
                    </div>
                    <div style={{ width: '100%', height: `${h}%`, background: subio ? '#FEE2E2' : i > 0 ? '#D1FAE5' : 'var(--teal-light)', borderRadius: '4px 4px 0 0', borderTop: `3px solid ${subio ? '#EF4444' : i > 0 ? '#10B981' : 'var(--teal)'}` }}></div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{c.fecha}</div>
                  </div>
                )
              })}
            </div>
            {minMax && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--line)' }}>
                <span>Mínimo histórico: <strong>${minMax.min.toFixed(2)}</strong></span>
                <span>Máximo histórico: <strong>${minMax.max.toFixed(2)}</strong></span>
                <span>Variación: <strong>{((minMax.max - minMax.min) / minMax.min * 100).toFixed(0)}%</strong></span>
              </div>
            )}
          </div>
        )}

        {/* Equivalencias */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 24 }}>
          <h2 className="section-head" style={{ margin: 0 }}>🔗 Equivalencias</h2>
          {!editandoGrupo && (
            <button
              onClick={() => { setGrupoInput(producto?.grupo_equivalencia || ''); setEditandoGrupo(true) }}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >{producto?.grupo_equivalencia ? '✏️ Cambiar grupo' : '+ Asignar grupo'}</button>
          )}
        </div>

        {!editandoGrupo ? (
          producto?.grupo_equivalencia ? (
            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  background: '#F3E8FF', color: '#7C3AED',
                  padding: '4px 10px', borderRadius: 999,
                  fontSize: 12, fontWeight: 800,
                }}>🔗 {producto.grupo_equivalencia}</span>
                <button
                  onClick={handleQuitarGrupo}
                  style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                >Sacar del grupo</button>
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
                Este producto es intercambiable con los siguientes. Sus stocks se suman al calcular la lista de compra.
              </div>
              {miembrosGrupo.filter(m => m.id !== producto.id).length === 0 ? (
                <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                  Solo este producto está en el grupo. Asigná el mismo grupo a otros productos para que se sumen.
                </div>
              ) : (
                <table className="t-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Producto</th>
                      <th>Pres.</th>
                      <th style={{ textAlign: 'right' }}>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miembrosGrupo.filter(m => m.id !== producto.id).map(m => (
                      <tr key={m.id}>
                        <td><Link to={`/admin/stock/${m.sku}`} style={{ color: '#1A3461', fontWeight: 700, textDecoration: 'none' }}>{m.sku}</Link></td>
                        <td>{m.nombre}{m.marca && <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>· {m.marca}</span>}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{m.presentacion || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{Number(m.stock_actual || 0)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#F3E8FF', fontWeight: 800 }}>
                      <td colSpan={3} style={{ textAlign: 'right' }}>Stock combinado del grupo</td>
                      <td style={{ textAlign: 'right', color: '#7C3AED' }}>
                        {miembrosGrupo.reduce((s, m) => s + Number(m.stock_actual || 0), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="card" style={{ color: 'var(--muted)', marginTop: 12 }}>
              Este producto no está agrupado. Si tenés otros con el mismo formato y función (ej: distintos aromas), asignáles el mismo grupo y la lista de compra los va a sumar.
            </div>
          )
        ) : (
          <div className="card" style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              Grupo de equivalencia
            </label>
            <input
              type="text"
              list="grupos-list"
              value={grupoInput}
              onChange={e => setGrupoInput(e.target.value)}
              placeholder="ej: brilha_lavarropas_2l"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
            />
            <datalist id="grupos-list">
              {gruposExistentes.map(g => <option key={g} value={g} />)}
            </datalist>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Usá el mismo nombre para todos los productos intercambiables. Se guarda en minúsculas, sin espacios.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleSaveGrupo}
                style={{ background: '#1A3461', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
              >Guardar</button>
              <button
                onClick={() => setEditandoGrupo(false)}
                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >Cancelar</button>
            </div>
          </div>
        )}

        {/* Uso en kits */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 24 }}>
          <h2 className="section-head" style={{ margin: 0 }}>📦 Se usa en {usoKits.length} kits</h2>
          <button
            className="btn"
            onClick={() => setKitModal({ mode: 'add', kitCodigo: '', cantidad: 1 })}
            style={{ background: '#1A3461', color: '#fff', padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            + Asignar a un kit
          </button>
        </div>
        {usoKits.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)', marginTop: 12 }}>Este producto no está en ningún kit. Tocá "Asignar a un kit" para agregarlo.</div>
        ) : (
          <table className="t-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Kit</th>
                <th>Tier</th>
                <th>Precio susc.</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right', width: 70 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usoKits.map(u => (
                <tr key={u.kit.codigo}>
                  <td><strong>{u.kit.nombre}</strong></td>
                  <td style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{u.kit.tier}</td>
                  <td>${Number(u.kit.precio_susc).toLocaleString('es-UY')}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>×{u.cantidad}</td>
                  <td style={{ textAlign: 'right', position: 'relative', overflow: 'visible' }}>
                    <KitFilaAcciones
                      onEditar={() => setKitModal({ mode: 'edit', kitCodigo: u.kit.codigo, kitNombre: u.kit.nombre, cantidad: Number(u.cantidad) })}
                      onQuitar={() => handleRemoveFromKit(u.kit.codigo, u.kit.nombre)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Modal: agregar / editar asignación a kit */}
        {kitModal && (
          <div
            onClick={() => setKitModal(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,31,61,.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 14, padding: 22, maxWidth: 440, width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,.25)'
              }}
            >
              <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 800, color: '#1A3461' }}>
                {kitModal.mode === 'edit' ? `Editar cantidad en ${kitModal.kitNombre}` : 'Asignar a un kit'}
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                {kitModal.mode === 'edit'
                  ? `Cuántas unidades de ${producto?.nombre} lleva este kit.`
                  : `Elegí en qué kit va ${producto?.nombre} y cuántas unidades por kit.`}
              </p>

              {kitModal.mode === 'add' && (
                <label style={{ display: 'block', marginBottom: 14 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Kit</span>
                  <select
                    value={kitModal.kitCodigo}
                    onChange={e => setKitModal({ ...kitModal, kitCodigo: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="">— Elegí un kit —</option>
                    {kitsDisponibles
                      .filter(k => !usoKits.some(u => u.kit.codigo === k.codigo))
                      .map(k => (
                        <option key={k.codigo} value={k.codigo}>
                          {k.nombre} · {k.tier}
                        </option>
                      ))}
                  </select>
                  {kitsDisponibles.filter(k => !usoKits.some(u => u.kit.codigo === k.codigo)).length === 0 && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>
                      Este producto ya está asignado a todos los kits.
                    </div>
                  )}
                </label>
              )}

              <label style={{ display: 'block', marginBottom: 16 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Cantidad por kit</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={kitModal.cantidad}
                  onChange={e => setKitModal({ ...kitModal, cantidad: e.target.value })}
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  En unidades reales del producto (botellas / rollos / unidades). Ej: si el kit lleva 1 L y la botella es 500 ml → poné 2.
                </div>
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setKitModal(null)}
                  style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveKitAssignment}
                  style={{ background: '#1A3461', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Movimientos */}
        <h2 className="section-head">🔄 Movimientos ({movs.length})</h2>
        {movs.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)' }}>Sin movimientos todavía.</div>
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right' }}>Costo u.</th>
                <th>Proveedor / Entrega</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {movs.map(m => {
                const tipoColor = {
                  entrada: { bg: '#D1FAE5', color: '#065F46', sign: '+' },
                  salida: { bg: '#FEE2E2', color: '#991B1B', sign: '−' },
                  ajuste: { bg: '#FEF3C7', color: '#92400E', sign: '~' },
                  devolucion: { bg: '#E0F2FE', color: '#0C4A6E', sign: '↩' }
                }[m.tipo] || { bg: 'var(--paper)', color: 'var(--muted)', sign: '?' }
                return (
                  <tr key={m.id}>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(m.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                    <td>
                      <span style={{ background: tipoColor.bg, color: tipoColor.color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        {tipoColor.sign} {m.tipo}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: tipoColor.color }}>
                      {m.tipo === 'salida' || (m.tipo === 'ajuste' && m.cantidad < 0) ? '−' : '+'}{Math.abs(Number(m.cantidad))}
                    </td>
                    <td style={{ textAlign: 'right' }}>{m.costo_unitario ? `$${Number(m.costo_unitario).toFixed(2)}` : '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {m.proveedor?.nombre && `📦 ${m.proveedor.nombre}`}
                      {m.entrega?.numero_orden && <code style={{ fontSize: 11, color: 'var(--muted)' }}>{m.entrega.numero_orden}</code>}
                      {m.referencia_externa && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Fact: {m.referencia_externa}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{m.notas || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        </>
        )}

        {editing && <EditModal producto={producto} proveedores={proveedores} onClose={() => setEditing(false)} onDone={(skuNuevo) => {
          setEditing(false)
          if (skuNuevo && skuNuevo !== producto.sku) navigate(`/admin/stock/${skuNuevo}`, { replace: true })
          else load()
        }} />}
        {editingFicha && <FichaTiendaModal producto={producto} proveedores={proveedores} onClose={() => setEditingFicha(false)} onDone={() => { setEditingFicha(false); load() }} />}
        {pausarModal && <PausarProductoModal producto={producto} onClose={() => setPausarModal(false)} onDone={() => { setPausarModal(false); load() }} />}
      </div>
    </AdminLayout>
  )
}

/* ─── Modal: pausar producto con warning de kits afectados ─── */
function PausarProductoModal({ producto, onClose, onDone }) {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    getKitsQueUsan(producto.id).then(k => { setKits(k); setLoading(false) })
  }, [producto.id])

  async function pausar() {
    setSaving(true); setErr(null)
    try {
      await pausarProducto(producto.id, true, motivo.trim() || null)
      onDone()
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const totalClientes = kits.reduce((s, k) => s + Number(k.clientes_activos || 0), 0)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 540, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>⏸️ Pausar producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          <code>{producto.sku}</code> · <strong>{producto.nombre}</strong>
        </p>

        {loading ? (
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 16 }}>Buscando kits afectados…</div>
        ) : kits.length > 0 ? (
          <div style={{ background: '#FEF2F2', border: '2px solid #FCA5A5', borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div>
                <strong style={{ color: '#7F1D1D', fontSize: 14 }}>Atención: este producto está en {kits.length} kit{kits.length !== 1 ? 's' : ''}</strong>
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>
                  Total <strong>{totalClientes}</strong> cliente{totalClientes !== 1 ? 's' : ''} suscripto{totalClientes !== 1 ? 's' : ''} reciben este producto cada mes. <strong>Tenés que conseguir un sustituto.</strong>
                </div>
              </div>
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13, color: '#7F1D1D' }}>
              {kits.map(k => (
                <li key={k.kit_codigo} style={{ marginBottom: 4 }}>
                  <strong>{k.kit_nombre}</strong> ({k.kit_tier}) — usa ×{k.cantidad}
                  {k.clientes_activos > 0 && <span> · <strong>{k.clientes_activos}</strong> clientes activos</span>}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#166534' }}>
            ✓ Este producto no está en ningún kit. Pausarlo solo lo saca de la Tienda.
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 14 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
            Motivo (opcional)
          </b>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
            placeholder="Ej: Drogería sin stock hasta el lunes 6/5" style={{ width: '100%' }} />
        </label>

        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn primary" onClick={pausar} disabled={saving}
            style={{ background: '#F59E0B', borderColor: '#F59E0B' }}>
            {saving ? 'Pausando…' : '⏸️ Confirmar pausa'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Modal para editar la Ficha de Tienda ─── */
function FichaTiendaModal({ producto, proveedores = [], onClose, onDone }) {
  const [form, setForm] = useState({
    descripcion: producto.descripcion || '',
    aroma:       producto.aroma || '',
    rendimiento: producto.rendimiento || '',
    beneficios:  Array.isArray(producto.beneficios) ? producto.beneficios.join('\n') : '',
    modo_uso:    producto.modo_uso || '',
    ideal_para:  producto.ideal_para || '',
    composicion: producto.composicion || '',
    proveedor_id: producto.proveedor?.id || producto.proveedor_id || '',
  })
  const [imagenUrl, setImagenUrl] = useState(producto.imagen_url || '')
  const [subiendo, setSubiendo] = useState(false)
  const [modoActivo, setModoActivo] = useState(null) // 'imagen' | 'texto' | 'todo'
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  // Refs para los inputs ocultos (para que cada botón abra su propio file picker)
  const fileInputRefs = {
    imagen: useRef(null),
    texto: useRef(null),
    todo: useRef(null),
  }

  function pickFile(modo) {
    setErr(null)
    fileInputRefs[modo].current?.click()
  }

  // Reduce foto a max 1280px + JPEG 0.88 antes de subir.
  // Evita WORKER_LIMIT_EXCEEDED por imágenes pesadas del iPad (5-10 MB).
  async function reducirEnNavegador(file, maxLado = 1280, calidad = 0.88) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onerror = () => reject(new Error('No se pudo leer la foto'))
      reader.onload = () => {
        const img = new Image()
        img.onerror = () => reject(new Error('Foto inválida o corrupta'))
        img.onload = () => {
          let { width: w, height: h } = img
          if (w > maxLado || h > maxLado) {
            const ratio = maxLado / Math.max(w, h)
            w = Math.round(w * ratio); h = Math.round(h * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = w; canvas.height = h
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, w, h)
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('No se pudo comprimir'))
              const reduced = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
              resolve(reduced)
            },
            'image/jpeg', calidad
          )
        }
        img.src = reader.result
      }
      reader.readAsDataURL(file)
    })
  }

  async function handleFoto(e, modo) {
    const fileOriginal = e.target.files?.[0]
    if (!fileOriginal) return
    setSubiendo(true); setModoActivo(modo); setErr(null)
    try {
      // Reducir foto en navegador si es > 800 KB
      let file = fileOriginal
      if (fileOriginal.size > 800 * 1024) {
        try {
          file = await reducirEnNavegador(fileOriginal, 1280, 0.88)
          console.log(`[foto] Reducida: ${(fileOriginal.size/1024).toFixed(0)}KB → ${(file.size/1024).toFixed(0)}KB`)
        } catch (e) {
          console.warn('[foto] No se pudo reducir, subiendo original:', e.message)
          file = fileOriginal
        }
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('sku', producto.sku)
      formData.append('modo', modo)  // 'imagen' | 'texto' | 'todo'

      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-producto`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: formData,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      if (data?.error) throw new Error(data.error)

      // Log detallado para debug
      console.log('[procesar-foto] respuesta:', {
        modo,
        flux_aplicado: data.flux_aplicado,
        flux_error: data.flux_error,
        remove_bg_aplicado: data.remove_bg_aplicado,
        campos_actualizados: data.campos_actualizados,
        imagen_url: data.imagen_url,
      })
      if ((modo === 'imagen' || modo === 'todo') && !data.flux_aplicado) {
        const errMsg = data.flux_error || 'Flux no se aplicó (REPLICATE_API_TOKEN faltante?)'
        console.warn('⚠️ Flux NO aplicó:', errMsg)
        setErr(`Foto subida pero sin escena premium. Razón: ${errMsg}`)
      }

      // Si procesó imagen, actualizar preview
      if (modo === 'imagen' || modo === 'todo') {
        setImagenUrl(data.imagen_url)
      }
      // Si analizó texto, actualizar form (solo campos vacíos en modo 'todo', todos en modo 'texto')
      if (modo === 'texto' || modo === 'todo') {
        const ficha = data.ficha || {}
        setForm(f => ({
          ...f,
          // En modo 'texto' siempre pisamos. En 'todo' solo si vacío.
          descripcion: (modo === 'texto' || !f.descripcion) ? (ficha.descripcion || f.descripcion) : f.descripcion,
          aroma:       (modo === 'texto' || !f.aroma)       ? (ficha.aroma       || f.aroma)       : f.aroma,
          rendimiento: (modo === 'texto' || !f.rendimiento) ? (ficha.rendimiento || f.rendimiento) : f.rendimiento,
          beneficios:  (modo === 'texto' || !f.beneficios)
            ? (Array.isArray(ficha.beneficios) && ficha.beneficios.length > 0 ? ficha.beneficios.join('\n') : f.beneficios)
            : f.beneficios,
          modo_uso:    (modo === 'texto' || !f.modo_uso)    ? (ficha.modo_uso    || f.modo_uso)    : f.modo_uso,
          ideal_para:  (modo === 'texto' || !f.ideal_para)  ? (ficha.ideal_para  || f.ideal_para)  : f.ideal_para,
          composicion: (modo === 'texto' || !f.composicion) ? (ficha.composicion || f.composicion) : f.composicion,
        }))
      }
    } catch (e) {
      setErr('Error procesando: ' + (e.message || String(e)))
    } finally {
      setSubiendo(false); setModoActivo(null)
      // Resetear input para permitir re-subir mismo archivo
      if (fileInputRefs[modo].current) fileInputRefs[modo].current.value = ''
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null); setSaving(true)
    try {
      const beneficios = form.beneficios.split('\n').map(s => s.trim()).filter(Boolean)
      // RPC distingue: '' = borrar el campo, null = mantener anterior.
      // Mandamos siempre el valor del form (incluyendo '' si el usuario lo vació).
      const { error } = await supabase.rpc('admin_actualizar_ficha_producto', {
        p_id: producto.id,
        p_imagen_url: imagenUrl ?? '',
        p_descripcion: form.descripcion ?? '',
        p_aroma: form.aroma ?? '',
        p_rendimiento: form.rendimiento ?? '',
        p_beneficios: beneficios.length > 0 ? beneficios : [],
        p_modo_uso: form.modo_uso ?? '',
        p_ideal_para: form.ideal_para ?? '',
        p_composicion: form.composicion ?? '',
      })
      if (error) throw error
      // Actualizar proveedor si cambió (la RPC de ficha no lo maneja)
      if ((producto.proveedor?.id || producto.proveedor_id || '') !== (form.proveedor_id || '')) {
        await actualizarProducto(producto.id, { proveedor_id: form.proveedor_id || null })
      }
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>🛍️ Ficha de Tienda</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          Esta info se ve en el modal de detalle del producto cuando el cliente clickea en la Tienda.
        </p>

        <form onSubmit={handleSubmit}>
          <Field label="Foto del producto">
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14, alignItems: 'start' }}>
              <div style={{ background: 'var(--paper, #f8fafc)', borderRadius: 10, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {imagenUrl
                  ? <img src={imagenUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 36, opacity: .35 }}>📦</span>}
              </div>
              <div>
                {/* Inputs ocultos — uno por cada modo */}
                <input ref={fileInputRefs.imagen} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'imagen')} style={{ display: 'none' }} />
                <input ref={fileInputRefs.texto}  type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'texto')}  style={{ display: 'none' }} />
                <input ref={fileInputRefs.todo}   type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'todo')}   style={{ display: 'none' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button type="button" onClick={() => pickFile('imagen')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #00A8B5', background: '#fff', color: '#1A3461', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'imagen' ? '🎨 Generando escena…' : '🖼️ Cambiar foto (mantiene texto)'}
                  </button>
                  <button type="button" onClick={() => pickFile('texto')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #1A5CB8', background: '#fff', color: '#1A3461', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'texto' ? '📋 Leyendo etiqueta…' : '📋 Leer etiqueta y autocompletar texto'}
                  </button>
                  <button type="button" onClick={() => pickFile('todo')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #00A8B5 0%, #1A5CB8 100%)', color: '#fff', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'todo' ? '✨ Procesando todo…' : '✨ Procesar todo (foto + texto)'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
                  {subiendo
                    ? '🤖 Subiendo y procesando con IA… (15-50 seg)'
                    : <>
                        🖼️ <strong>Cambiar foto</strong>: regenera escena premium · NO toca texto<br/>
                        📋 <strong>Leer etiqueta</strong>: extrae info de la foto · NO toca imagen<br/>
                        ✨ <strong>Procesar todo</strong>: producto nuevo desde cero
                      </>}
                </div>
              </div>
            </div>
          </Field>

          <Field label="Proveedor">
            <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id: e.target.value})}>
              <option value="">— sin proveedor —</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Field>

          <Field label="Descripción corta">
            <textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})}
              rows="2" placeholder="Ej: Suavizante premium con perfume duradero. Su fórmula concentrada rinde hasta 30 lavados..." />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Aroma / Variante">
              <input type="text" value={form.aroma} onChange={e => setForm({...form, aroma: e.target.value})}
                placeholder="Ej: Blue Soft (floral azul)" />
            </Field>
            <Field label="Rendimiento">
              <input type="text" value={form.rendimiento} onChange={e => setForm({...form, rendimiento: e.target.value})}
                placeholder="Ej: Hasta 30 lavados" />
            </Field>
          </div>

          <Field label="Beneficios (uno por línea — los muestra como ✓ en la ficha)">
            <textarea value={form.beneficios} onChange={e => setForm({...form, beneficios: e.target.value})}
              rows="4" placeholder={'Acción anti pelusas\n4× más perfume\nBase 100% vegetal\nEnvase reciclable'} />
          </Field>

          <Field label="Ideal para">
            <input type="text" value={form.ideal_para} onChange={e => setForm({...form, ideal_para: e.target.value})}
              placeholder="Ej: Ropa de color y delicada" />
          </Field>

          <Field label="Modo de uso">
            <textarea value={form.modo_uso} onChange={e => setForm({...form, modo_uso: e.target.value})}
              rows="2" placeholder="Ej: Agregar 30 ml al cajón del suavizante por carga." />
          </Field>

          <Field label="Composición">
            <input type="text" value={form.composicion} onChange={e => setForm({...form, composicion: e.target.value})}
              placeholder="Ej: Base 100% vegetal · Sin fosfatos" />
          </Field>

          {err && <div className="err-box">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={saving || subiendo}>
              {saving ? 'Guardando...' : 'Guardar ficha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditModal({ producto, proveedores, onClose, onDone }) {
  const etiquetasIniciales = Array.isArray(producto.etiquetas_urls) ? producto.etiquetas_urls : []
  const ivaInicial = producto.iva_pct ?? 22
  // Multi-categoría: si el producto trae el array categorias[] lo usamos, sino lo derivamos de categoria principal
  const categoriasIniciales = Array.isArray(producto.categorias) && producto.categorias.length > 0
    ? producto.categorias
    : (producto.categoria ? [producto.categoria] : [])
  const [form, setForm] = useState({
    sku:            producto.sku,
    nombre:         producto.nombre,
    marca:          producto.marca || '',
    presentacion:   producto.presentacion || '',
    categoria:      producto.categoria || '',  // principal
    categorias:     categoriasIniciales,        // todas las marcadas (incluye la principal)
    tier:           producto.tier || 'C',
    iva_pct:        ivaInicial,
    // Regla c/IVA: tanto costo como precio se cargan y guardan c/IVA directo, sin conversiones.
    costo_unitario: producto.costo_unitario != null ? String(producto.costo_unitario) : '',
    precio_publico: producto.precio_publico != null ? String(producto.precio_publico) : '',
    precio_mercado: producto.precio_mercado != null ? String(producto.precio_mercado) : '',
    stock_minimo:   producto.stock_minimo ?? 0,
    stock_critico:  producto.stock_critico ?? 0,
    proveedor_id:   producto.proveedor?.id || producto.proveedor_id || '',
    vendible_suelto: producto.vendible_suelto !== false,
    notas:          producto.notas || '',
    // Datos físicos del producto (para cálculo de envío en ML)
    peso_g:                producto.peso_g != null ? String(producto.peso_g) : '',
    largo_cm:              producto.largo_cm != null ? String(producto.largo_cm) : '',
    ancho_cm:              producto.ancho_cm != null ? String(producto.ancho_cm) : '',
    alto_cm:               producto.alto_cm != null ? String(producto.alto_cm) : '',
    bolsa_recomendada_id:  producto.bolsa_recomendada_id || '',
  })
  const [bolsas, setBolsas] = useState([])
  useEffect(() => {
    supabase.from('v_bolsas_envio').select('id, sku, peso_vacia_g, capacidad_max_kg, capacidad_max_litros').then(({ data }) => setBolsas(data || []))
  }, [])
  const [etiquetasExistentes, setEtiquetasExistentes] = useState(etiquetasIniciales)
  // Nuevas etiquetas a sumar (cada item: { file: File, preview: string })
  const [nuevasEtiquetas, setNuevasEtiquetas] = useState([])
  const [reanalizando, setReanalizando] = useState(false)
  const [fichaIA, setFichaIA] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  function handleNuevasEtiquetas(e) {
    const nuevos = Array.from(e.target.files || [])
    if (!nuevos.length) return
    setNuevasEtiquetas(prev => [
      ...prev,
      ...nuevos.map(file => ({ file, preview: URL.createObjectURL(file) })),
    ])
    e.target.value = ''
  }
  function quitarNueva(idx) {
    setNuevasEtiquetas(prev => {
      const it = prev[idx]
      if (it?.preview) URL.revokeObjectURL(it.preview)
      return prev.filter((_, i) => i !== idx)
    })
  }
  function quitarExistente(idx) {
    if (!confirm('¿Quitar esta foto de etiqueta? La URL se va a desvincular del producto al guardar.')) return
    setEtiquetasExistentes(prev => prev.filter((_, i) => i !== idx))
  }

  // Re-analizar con IA usando la imagen principal + las nuevas etiquetas (no se guarda nada hasta apretar "Guardar").
  async function reanalizarIA() {
    if (!producto.imagen_url && nuevasEtiquetas.length === 0) {
      setErr('Necesitás imagen del producto o al menos una etiqueta nueva para re-analizar.')
      return
    }
    setReanalizando(true); setErr(null)
    try {
      const fd = new FormData()
      // Si no hay foto local, usamos la URL existente bajándola.
      let mainFile = null
      if (producto.imagen_url) {
        const r = await fetch(producto.imagen_url)
        const blob = await r.blob()
        mainFile = new File([blob], `${producto.sku}.jpg`, { type: blob.type || 'image/jpeg' })
      }
      if (!mainFile && nuevasEtiquetas.length > 0) {
        mainFile = nuevasEtiquetas[0].file
      }
      if (!mainFile) throw new Error('Sin imagen para analizar')
      fd.append('file', mainFile)
      nuevasEtiquetas.forEach((it, i) => fd.append(`file_etiqueta_${i}`, it.file))

      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analizar-foto-producto`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: fd,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      const f = data.ficha || {}
      setFichaIA(f)
      // Solo prellena campos que están vacíos para no pisar correcciones manuales.
      setForm(prev => ({
        ...prev,
        nombre:       prev.nombre || f.nombre || prev.nombre,
        marca:        prev.marca || f.marca || prev.marca,
        presentacion: prev.presentacion || f.presentacion || prev.presentacion,
        categoria:    prev.categoria || f.categoria || prev.categoria,
        tier:         prev.tier || f.tier || prev.tier,
      }))
    } catch (e) {
      setErr('No se pudo re-analizar: ' + e.message)
    } finally { setReanalizando(false) }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!form.sku.trim()) { setErr('Falta SKU'); return }
    if (!form.nombre.trim()) { setErr('Falta nombre'); return }
    if (!form.categorias || form.categorias.length === 0) { setErr('Marcá al menos 1 categoría'); return }
    if (!form.categoria) { setErr('Elegí cuál de las categorías es la principal (botón "principal")'); return }
    if (!form.categorias.includes(form.categoria)) { setErr('La categoría principal debe estar marcada'); return }
    setSaving(true)
    try {
      // 1) Editar datos básicos (incluye validación de duplicado de SKU server-side).
      // Precios: el form los tiene s/IVA, los convertimos a c/IVA usando form.iva_pct para guardar en DB.
      await editarProducto(producto.id, {
        sku: form.sku.trim().toUpperCase(),
        nombre: form.nombre.trim(),
        categoria: form.categoria,
        tier: form.tier,
        marca: form.marca.trim() || null,
        presentacion: form.presentacion.trim() || null,
        iva_pct: Number(form.iva_pct) || 22,
        // Regla c/IVA: guardar tal cual lo que el user cargó (sin convertir IVA)
        costo_unitario: form.costo_unitario === '' || form.costo_unitario == null ? null : Number(form.costo_unitario),
        precio_publico: form.precio_publico === '' || form.precio_publico == null ? null : Number(form.precio_publico),
        precio_mercado: form.precio_mercado === '' || form.precio_mercado == null ? null : Number(form.precio_mercado),
        stock_minimo: form.stock_minimo === '' ? null : parseFloat(form.stock_minimo),
        stock_critico: form.stock_critico === '' ? null : parseFloat(form.stock_critico),
        proveedor_id: form.proveedor_id || null,
        vendible_suelto: form.vendible_suelto,
        notas: form.notas.trim() || null,
      })

      // Datos físicos: la RPC admin_editar_producto no los acepta, usamos RPC dedicada
      // que es SECURITY DEFINER (bypasea RLS productos que solo deja UPDATE a superadmin).
      const { error: errFis } = await supabase.rpc('admin_actualizar_datos_fisicos', {
        p_id: producto.id,
        p_peso_g: form.peso_g === '' ? null : Number(form.peso_g),
        p_largo_cm: form.largo_cm === '' ? null : Number(form.largo_cm),
        p_ancho_cm: form.ancho_cm === '' ? null : Number(form.ancho_cm),
        p_alto_cm: form.alto_cm === '' ? null : Number(form.alto_cm),
        p_bolsa_recomendada_id: form.bolsa_recomendada_id || null,
      })
      if (errFis) {
        console.error('Error guardando datos físicos:', errFis)
        throw new Error('Datos físicos no se guardaron: ' + errFis.message)
      }

      const skuNuevo = form.sku.trim().toUpperCase()

      // 1b) Sincronizar tabla pivot producto_categorias (multi-categoría)
      const { error: errCats } = await supabase.rpc('set_producto_categorias', {
        p_producto_id: producto.id,
        p_categorias: form.categorias,
        p_principal: form.categoria,
      })
      if (errCats) throw errCats

      // 2) Si quitaron etiquetas existentes, persistir el array recortado.
      if (etiquetasExistentes.length !== etiquetasIniciales.length) {
        await actualizarProducto(producto.id, { etiquetas_urls: etiquetasExistentes })
      }

      // 3) Si hay nuevas etiquetas, las subimos via procesar-foto-producto y se concatenan al array.
      if (nuevasEtiquetas.length > 0) {
        try {
          const fd = new FormData()
          // Necesita una foto principal: si tenemos imagen_url la bajamos; si no, la primera nueva.
          let mainFile = null
          if (producto.imagen_url) {
            const r = await fetch(producto.imagen_url)
            const blob = await r.blob()
            mainFile = new File([blob], `${skuNuevo}.jpg`, { type: blob.type || 'image/jpeg' })
          } else {
            mainFile = nuevasEtiquetas[0].file
          }
          fd.append('file', mainFile)
          fd.append('sku', skuNuevo)
          nuevasEtiquetas.forEach((it, i) => fd.append(`file_etiqueta_${i}`, it.file))
          if (fichaIA) fd.append('ficha', JSON.stringify(fichaIA))
          // Para preservar las etiquetas que ya tenía (procesar-foto-producto reemplaza el array),
          // mandamos el array previo concatenado en una columna aparte y lo re-aplicamos después.
          const { data: { session } } = await supabase.auth.getSession()
          const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-producto`
          const resp = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token || ''}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: fd,
          })
          const data = await resp.json().catch(() => ({}))
          if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
          // Mergear: las nuevas que devuelve la function + las que el usuario quiso conservar.
          const subidas = Array.isArray(data?.etiquetas_urls) ? data.etiquetas_urls : []
          const merged = [...etiquetasExistentes, ...subidas]
          if (merged.length > 0) {
            await actualizarProducto(producto.id, { etiquetas_urls: merged })
          }
        } catch (e) {
          alert('Datos guardados, pero no se pudieron subir las nuevas etiquetas: ' + e.message)
        }
      }

      onDone(skuNuevo)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const skuCambia = form.sku.trim().toUpperCase() !== producto.sku

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>✏️ Editar producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          Editás todos los datos básicos. Si subís nuevas fotos de etiqueta, podés re-analizar con IA antes de guardar.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Bloque etiquetas: existentes + nuevas */}
          <div style={{ background: '#F8F6F1', border: '2px dashed #00A8B5', borderRadius: 12, padding: 16, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <strong style={{ fontSize: 13, color: '#1A3461' }}>🏷️ Fotos de etiqueta</strong>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {etiquetasExistentes.length} guardadas · {nuevasEtiquetas.length} nuevas
              </span>
            </div>

            {(etiquetasExistentes.length > 0 || nuevasEtiquetas.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 6, marginBottom: 10 }}>
                {etiquetasExistentes.map((url, i) => (
                  <div key={`ex-${i}`} style={{ position: 'relative', background: '#fff', borderRadius: 8, height: 100, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                    <img src={url} alt={`Etiqueta ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => quitarExistente(i)} title="Quitar"
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(26,26,26,.85)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>×</button>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(26,52,97,.85)', color: '#fff', fontSize: 10, padding: '2px 6px', fontWeight: 700 }}>guardada</div>
                  </div>
                ))}
                {nuevasEtiquetas.map((it, i) => (
                  <div key={`new-${i}`} style={{ position: 'relative', background: '#fff', borderRadius: 8, height: 100, overflow: 'hidden', border: '2px solid #00A8B5' }}>
                    <img src={it.preview} alt={`Nueva ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => quitarNueva(i)} title="Quitar"
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(26,26,26,.85)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>×</button>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#00A8B5', color: '#fff', fontSize: 10, padding: '2px 6px', fontWeight: 700 }}>nueva</div>
                  </div>
                ))}
              </div>
            )}

            <input type="file" accept="image/jpeg,image/png,image/webp" multiple
              onChange={handleNuevasEtiquetas} disabled={reanalizando || saving}
              style={{ width: '100%', fontSize: 11 }} />

            {nuevasEtiquetas.length > 0 && (
              <button type="button" className="btn ghost" onClick={reanalizarIA}
                disabled={reanalizando || saving}
                style={{ marginTop: 10, width: '100%', borderColor: '#1A3461', color: '#1A3461' }}>
                {reanalizando ? '🤖 Re-analizando con IA…' : '✨ Re-analizar con IA (foto + nuevas etiquetas)'}
              </button>
            )}
            {fichaIA && (
              <div style={{ fontSize: 11, color: '#00A8B5', marginTop: 6, fontWeight: 700, textAlign: 'center' }}>
                ✓ Datos detectados — campos vacíos prellenados, los completos quedaron como estaban.
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <Field label="SKU" required>
              <input type="text" required value={form.sku}
                onChange={e => setForm({...form, sku: e.target.value.toUpperCase()})}
                maxLength={20} style={{ fontFamily: 'monospace' }} />
              {skuCambia && (
                <div style={{ fontSize: 11, color: '#92400E', marginTop: 4 }}>
                  ⚠️ Cambiás SKU de <code>{producto.sku}</code> → <code>{form.sku.trim().toUpperCase()}</code>. Se valida que no haya otro igual.
                </div>
              )}
            </Field>
            <Field label="Nombre" required>
              <input type="text" required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Marca">
              <input type="text" value={form.marca} onChange={e => setForm({...form, marca: e.target.value})} />
            </Field>
            <Field label="Presentación">
              <input type="text" value={form.presentacion} onChange={e => setForm({...form, presentacion: e.target.value})} placeholder="1 L · 500 mL · 12 unid" />
            </Field>
          </div>

          {/* ════ Datos físicos / envío ════ */}
          <div style={{
            marginTop: 16,
            padding: '14px 16px',
            background: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#1A3461', marginBottom: 10 }}>
              📐 Datos físicos · usado por ML para calcular envío
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1.4fr', gap: 10 }}>
              <Field label="Peso (g) c/envase">
                <input type="number" min="0" step="1" value={form.peso_g}
                  onChange={e => setForm({...form, peso_g: e.target.value})}
                  placeholder="Ej: 1050" />
              </Field>
              <Field label="Largo (cm)">
                <input type="number" min="0" step="0.1" value={form.largo_cm}
                  onChange={e => setForm({...form, largo_cm: e.target.value})}
                  placeholder="Ej: 25" />
              </Field>
              <Field label="Ancho (cm)">
                <input type="number" min="0" step="0.1" value={form.ancho_cm}
                  onChange={e => setForm({...form, ancho_cm: e.target.value})}
                  placeholder="Ej: 10" />
              </Field>
              <Field label="Alto (cm)">
                <input type="number" min="0" step="0.1" value={form.alto_cm}
                  onChange={e => setForm({...form, alto_cm: e.target.value})}
                  placeholder="Ej: 8" />
              </Field>
              <Field label="Bolsa envío">
                <select value={form.bolsa_recomendada_id}
                  onChange={e => setForm({...form, bolsa_recomendada_id: e.target.value})}>
                  <option value="">↻ Auto (según peso/volumen)</option>
                  {bolsas.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.sku === 'PACK_CHICO' ? 'Chica 50×60' : 'Grande 66×100'} · hasta {b.capacidad_max_kg}kg / {b.capacidad_max_litros}L
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {(() => {
              const pesoKg = (Number(form.peso_g) || 0) / 1000
              const litros = ((Number(form.largo_cm) || 0) * (Number(form.ancho_cm) || 0) * (Number(form.alto_cm) || 0)) / 1000
              const completo = pesoKg > 0 && litros > 0
              if (!completo) return (
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                  💡 Sin estos datos el wizard ML no puede calcular tarifa Mercado Envíos automática.
                </div>
              )
              const volVolumetricoKg = litros / 6
              const cobraML = Math.max(pesoKg, volVolumetricoKg)
              return (
                <div style={{ fontSize: 11.5, color: '#1A3461', marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <span>📦 Peso real: <b>{pesoKg.toFixed(2)} kg</b></span>
                  <span>📐 Volumen: <b>{litros.toFixed(2)} L</b></span>
                  <span>⚖️ Volumétrico ML: <b>{volVolumetricoKg.toFixed(2)} kg</b></span>
                  <span style={{ color: cobraML === pesoKg ? '#10B981' : '#854F0B' }}>
                    💰 ML cobra envío por: <b>{cobraML.toFixed(2)} kg</b> ({cobraML === pesoKg ? 'peso real ✓' : 'volumen ⚠️'})
                  </span>
                </div>
              )
            })()}
          </div>

          <Field label="Categorías (podés elegir varias)" required>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8, padding: '8px 0' }}>
              {CATEGORIAS.map(c => {
                const checked = form.categorias.includes(c.v)
                const esPrincipal = form.categoria === c.v
                return (
                  <label key={c.v} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px',
                    background: checked ? (esPrincipal ? '#dcfce7' : '#f0fcfd') : '#fff',
                    border: `1px solid ${esPrincipal ? '#22c55e' : checked ? '#b3eef3' : '#e2e8f0'}`,
                    borderRadius: 8, cursor: 'pointer', fontSize: 13
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => {
                        const nuevas = e.target.checked
                          ? [...form.categorias, c.v]
                          : form.categorias.filter(x => x !== c.v)
                        // Si destildamos la principal, la principal pasa a la primera de las restantes
                        let nuevaPrincipal = form.categoria
                        if (!nuevas.includes(form.categoria)) nuevaPrincipal = nuevas[0] || ''
                        if (e.target.checked && !form.categoria) nuevaPrincipal = c.v
                        setForm({ ...form, categorias: nuevas, categoria: nuevaPrincipal })
                      }}
                    />
                    <span style={{ flex: 1 }}>{c.label}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={(ev) => { ev.preventDefault(); setForm({ ...form, categoria: c.v }) }}
                        title={esPrincipal ? 'Esta es la categoría principal' : 'Marcar como principal'}
                        style={{
                          fontSize: 11, padding: '2px 8px', border: 'none', borderRadius: 4,
                          background: esPrincipal ? '#22c55e' : '#e2e8f0',
                          color: esPrincipal ? '#fff' : '#475569', cursor: 'pointer', fontWeight: 600
                        }}
                      >
                        {esPrincipal ? '★ principal' : 'principal'}
                      </button>
                    )}
                  </label>
                )
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
              💡 La <b>principal</b> (verde) es la que se usa en filtros de admin y reportes. El producto aparece en la tienda en <b>todas</b> las categorías marcadas.
            </div>
          </Field>
          <Field label="Tier">
            <select value={form.tier} onChange={e => setForm({...form, tier: e.target.value})}>
              {TIERS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <h3 className="section-head" style={{ marginTop: 18 }}>💰 Precios</h3>
          <div style={{ background: '#f0fcfd', border: '1px solid #b3eef3', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: 'var(--navy-ink)', marginBottom: 10 }}>
            💡 Cargá los precios <b>con IVA</b> (es lo que ve el cliente). Definí el IVA del producto abajo · el sistema muestra abajo de cada campo el equivalente sin IVA para que lo uses en planillas / contabilidad.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
            <Field label="IVA del producto">
              <select value={form.iva_pct} onChange={e => setForm({...form, iva_pct: Number(e.target.value)})}>
                <option value={22}>22% · General</option>
                <option value={10}>10% · Canasta básica</option>
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Costo unitario c/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.costo_unitario} onChange={e => setForm({...form, costo_unitario: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(form.costo_unitario ? Number(form.costo_unitario) / (1 + Number(form.iva_pct || 22)/100) : 0)} s/IVA {form.iva_pct}%</div>
            </Field>
            <Field label="Precio público c/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.precio_publico} onChange={e => setForm({...form, precio_publico: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(form.precio_publico ? Number(form.precio_publico) / (1 + Number(form.iva_pct || 22)/100) : 0)} s/IVA {form.iva_pct}%</div>
            </Field>
            <Field label="Precio mercado c/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.precio_mercado} onChange={e => setForm({...form, precio_mercado: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(form.precio_mercado ? Number(form.precio_mercado) / (1 + Number(form.iva_pct || 22)/100) : 0)} s/IVA {form.iva_pct}%</div>
            </Field>
          </div>

          <h3 className="section-head">📦 Stock</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Stock mínimo">
              <input type="number" min="0" step="1" value={form.stock_minimo} onChange={e => setForm({...form, stock_minimo: e.target.value})} />
            </Field>
            <Field label="Stock crítico">
              <input type="number" min="0" step="1" value={form.stock_critico} onChange={e => setForm({...form, stock_critico: e.target.value})} />
            </Field>
            <Field label="Proveedor">
              <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id: e.target.value})}>
                <option value="">— sin proveedor —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Disponibilidad">
            <label style={{ fontSize: 14 }}>
              <input type="checkbox" checked={form.vendible_suelto} onChange={e => setForm({...form, vendible_suelto: e.target.checked})} style={{ width: 'auto', marginRight: 8 }} />
              Vender suelto en La Tienda (no solo en kits)
            </label>
          </Field>

          <Field label="Notas internas">
            <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" placeholder="Ej: Sustituir por X si Boyerco no tiene stock" />
          </Field>

          {err && <div className="err-box">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={saving || reanalizando}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}

/* ─── Tab ML: panel de edición modo borrador
 *
 * CRUD local sobre la publicación ML. Los cambios se acumulan en `draft` y
 * sólo se empujan a ML cuando el usuario aprieta "Sincronizar a ML".
 *
 * Por qué borrador y no auto-sync: ML penaliza updates muy seguidos, y queremos
 * preview de neto/markup antes de empujar precio.
 * ──────────────────────────────────────────────────────────── */
function MlBorradorPanel({ producto, mlPub, mlConfig, draft, setDraft, syncing, onSync, onDiscard }) {
  // Valor efectivo = draft (si hay) o el valor actual de mlPub
  const v = (campo) => draft[campo] !== undefined ? draft[campo] : mlPub[campo]
  const cambiar = (campo, valor) => {
    // Si el nuevo valor es igual al de mlPub, sacamos del draft (no es cambio)
    const limpio = { ...draft }
    if (valor === mlPub[campo] || valor === '' || valor == null) {
      delete limpio[campo]
    } else {
      limpio[campo] = valor
    }
    setDraft(limpio)
  }
  const nPendientes = Object.keys(draft).length

  // Calculadora neto en vivo (igual que wizard PublicarProductoML Step 7)
  const precioML = Number(v('precio') || 0)
  const cm = Number(mlConfig?.comision_pct ?? 13.5)
  const rango = (mlConfig?.rangos || []).slice().sort((a, b) => a.hasta - b.hasta).find(r => precioML < r.hasta)
  const comision = Math.round(precioML * cm / 100)
  const costoFijo = rango ? rango.fijo : 0
  const neto = Math.round(precioML - comision - costoFijo)
  const precioWeb = Number(producto?.precio_publico || 0)
  const difWeb = neto - precioWeb

  return (
    <div style={{ marginTop: 18, background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#1A3461' }}>Editar publicación · modo borrador</h3>
          <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
            Cambiá lo que necesites. Nada se envía a ML hasta que aprietes <strong>Sincronizar</strong>.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {nPendientes > 0 && (
            <button
              onClick={onDiscard}
              disabled={syncing}
              style={{
                background: '#fff', border: '1px solid #CBD5E1', color: '#475569',
                padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >Descartar cambios</button>
          )}
          <button
            onClick={onSync}
            disabled={syncing || nPendientes === 0}
            style={{
              background: nPendientes > 0 ? '#3B82F6' : '#CBD5E1',
              color: '#fff', border: 'none',
              padding: '8px 16px', borderRadius: 8,
              fontSize: 13, fontWeight: 700,
              cursor: nPendientes > 0 && !syncing ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {syncing ? 'Sincronizando…' : nPendientes > 0 ? `Sincronizar a ML (${nPendientes})` : 'Sin cambios'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {/* Título */}
        <Field label={`Título ML (${(v('titulo') || '').length}/60)`}>
          <input
            type="text"
            value={v('titulo') || ''}
            onChange={e => cambiar('titulo', e.target.value.slice(0, 60))}
            maxLength={60}
            style={{
              width: '100%', padding: '10px 12px',
              border: `1px solid ${draft.titulo !== undefined ? '#3B82F6' : '#CBD5E1'}`,
              borderRadius: 8, fontSize: 13,
              background: draft.titulo !== undefined ? '#EFF6FF' : '#fff',
            }}
          />
        </Field>

        {/* Precio */}
        <Field label="Precio ML (c/IVA)">
          <input
            type="number"
            min="0"
            step="1"
            value={v('precio') ?? ''}
            onChange={e => cambiar('precio', e.target.value === '' ? null : Number(e.target.value))}
            style={{
              width: '100%', padding: '10px 12px',
              border: `1px solid ${draft.precio !== undefined ? '#3B82F6' : '#CBD5E1'}`,
              borderRadius: 8, fontSize: 13,
              background: draft.precio !== undefined ? '#EFF6FF' : '#fff',
            }}
          />
          {precioML > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
              Comisión {cm}%: <strong style={{ color: '#DC2626' }}>−${comision.toLocaleString('es-UY')}</strong>
              {costoFijo > 0 && <> · Fijo: <strong style={{ color: '#DC2626' }}>−${costoFijo}</strong></>}
              {' · '}Neto: <strong style={{ color: '#1A3461' }}>${neto.toLocaleString('es-UY')}</strong>
              {precioWeb > 0 && (
                <> · vs web: <strong style={{ color: difWeb >= 0 ? '#10B981' : '#DC2626' }}>
                  {difWeb >= 0 ? '+' : ''}${difWeb.toLocaleString('es-UY')}
                </strong></>
              )}
            </div>
          )}
          {difWeb < 0 && precioWeb > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 6, fontSize: 11, color: '#7F1D1D' }}>
              ⚠ El neto está <strong>${Math.abs(difWeb)}</strong> debajo del precio web. Subí markup o vendelo como pack.
            </div>
          )}
        </Field>

        {/* Stock publicado (en ML se llama available_quantity) */}
        <Field label="Stock publicado en ML">
          <input
            type="number"
            min="0"
            step="1"
            value={v('available_quantity') ?? ''}
            onChange={e => cambiar('available_quantity', e.target.value === '' ? null : Number(e.target.value))}
            style={{
              width: '100%', padding: '10px 12px',
              border: `1px solid ${draft.available_quantity !== undefined ? '#3B82F6' : '#CBD5E1'}`,
              borderRadius: 8, fontSize: 13,
              background: draft.available_quantity !== undefined ? '#EFF6FF' : '#fff',
            }}
          />
          <div style={{ marginTop: 4, fontSize: 11, color: '#94A3B8' }}>
            Stock local: <strong>{producto?.stock_actual ?? 0} u.</strong> · Reservado: {producto?.stock_reservado ?? 0}
          </div>
        </Field>

        {/* Status */}
        <Field label="Estado publicación">
          <select
            value={v('status') || 'active'}
            onChange={e => cambiar('status', e.target.value)}
            style={{
              width: '100%', padding: '10px 12px',
              border: `1px solid ${draft.status !== undefined ? '#3B82F6' : '#CBD5E1'}`,
              borderRadius: 8, fontSize: 13,
              background: draft.status !== undefined ? '#EFF6FF' : '#fff',
            }}
          >
            <option value="active">Activa (visible en ML)</option>
            <option value="paused">Pausada (oculta)</option>
            <option value="closed">Cerrada (no recuperable)</option>
          </select>
        </Field>

        {/* Free shipping: por default lo paga el comprador */}
        <Field label="Costo del envío">
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px',
            border: `1px solid ${draft.free_shipping !== undefined ? '#3B82F6' : '#CBD5E1'}`,
            borderRadius: 8,
            background: draft.free_shipping !== undefined ? '#EFF6FF' : '#fff',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={!!v('free_shipping')}
              onChange={e => cambiar('free_shipping', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#1A3461', fontWeight: 700 }}>
                {v('free_shipping') ? 'Limpack absorbe el envío' : 'El comprador paga el envío'}
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                {v('free_shipping')
                  ? '⚠ ML descuenta el costo del envío del precio que recibís. Conviene en productos caros (>$500).'
                  : 'Default. ML cobra el envío al comprador arriba del precio del producto.'}
              </div>
            </div>
          </label>
        </Field>
      </div>

      {/* ── Descripción rica ── */}
      <MlDescripcionEditor mlPub={mlPub} draft={draft} cambiar={cambiar} v={v} />

      {/* ── Galería 12 fotos (reusa ml_fotos + bucket productos-fotos-ml del wizard) ── */}
      <MlGaleriaEditor producto={producto} draft={draft} setDraft={setDraft} />

      {/* ── Atributos catálogo ── */}
      <MlAtributosEditor mlPub={mlPub} draft={draft} setDraft={setDraft} />

      {/* Footer: read-only metadata */}
      <div style={{
        marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--line)',
        display: 'flex', gap: 14, fontSize: 11, color: '#94A3B8', flexWrap: 'wrap',
      }}>
        {mlPub.ml_item_id && <span>ML item: <code>{mlPub.ml_item_id}</code></span>}
        {mlPub.category_id && <span>Categoría: <code>{mlPub.category_id}</code></span>}
        {mlPub.published_at && <span>Publicado: {new Date(mlPub.published_at).toLocaleDateString('es-UY')}</span>}
        {mlPub.permalink && (
          <a href={mlPub.permalink} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', marginLeft: 'auto', fontWeight: 700 }}>
            ↗ Ver en ML
          </a>
        )}
      </div>
    </div>
  )
}

/* ─── Sub-componente: editor de descripción rica ML
 *
 * La descripción tiene 2 partes:
 *  - MODIFICABLE: lo que escribe Nico (textarea). Va arriba.
 *  - FIJA: bloques institucionales (horario, envíos, pagos, sobre Limpack)
 *    que SIEMPRE se anexan al final. Leídos de app_config.ml_descripcion_fija
 *    para editar sin deploy.
 *
 * ML no recomienda emoticones — se sacaron del copy.
 * ──────────────────────────────────────────────────────────── */
// Fallback hardcoded si app_config no responde (espejo del valor real en BD)
const DESCRIPCION_FIJA_FALLBACK = [
  'HORARIO DE ATENCIÓN\nLunes a Viernes de 9:00 a 18:00 hs. Online.',
  'ENVÍOS\nLlegamos a todo Uruguay con Mercado Envíos:\n- Montevideo: Flex el mismo día.\n- Interior: Estándar en 2-5 días hábiles a tu agencia más cercana o domicilio.',
  'MEDIOS DE PAGO\nTodos los que acepta Mercado Pago: tarjetas de crédito (con cuotas sin recargo según promo vigente), débito, Abitab, Red Pagos, transferencia bancaria.',
  'PRECIOS\nPesos uruguayos. IVA incluido.',
  'SOBRE LIMPACK\nLimpieza, resuelta cada mes.\n\nEstamos en Montevideo y llevamos los productos de limpieza para tu hogar a domicilio. Mejor precio que el promedio del mercado.\n\nSuscripción mensual a un kit a medida: elegí el que más se ajusta a vos y tu familia, sin permanencia, pausá cuando quieras.\n\nTambién vendemos productos sueltos como este. Los chequeamos uno por uno antes de venderlos.',
]

function MlDescripcionEditor({ mlPub, draft, cambiar, v }) {
  const [bloquesFijos, setBloquesFijos] = useState(DESCRIPCION_FIJA_FALLBACK)

  // Cargar el bloque fijo desde app_config (editable sin deploy)
  useEffect(() => {
    supabase.from('app_config').select('value').eq('key', 'ml_descripcion_fija').maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          // Aceptamos tanto array directo como string JSON-quoted (helper cleanString del proyecto)
          const v = typeof data.value === 'string' ? JSON.parse(data.value) : data.value
          if (Array.isArray(v) && v.length > 0) setBloquesFijos(v)
        }
      })
  }, [])

  const valor = v('descripcion') ?? ''
  const dirty = draft.descripcion !== undefined
  const chars = (valor || '').length
  const totalChars = chars + bloquesFijos.join('\n\n').length + 4

  return (
    <div style={{ marginTop: 14, padding: 14, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 12, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          📝 Descripción ML
        </strong>
        <span style={{ fontSize: 11, color: totalChars > 50000 ? '#DC2626' : '#94A3B8' }}>
          {chars.toLocaleString('es-UY')} editables + fijas = {totalChars.toLocaleString('es-UY')} / 50.000
        </span>
      </div>

      {/* PARTE MODIFICABLE */}
      <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: '#1A3461', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
        ✏️ Parte editable del producto
      </label>
      <textarea
        value={valor}
        onChange={e => cambiar('descripcion', e.target.value)}
        placeholder="Descripción específica del producto que ve el comprador. Podés usar saltos de línea y emojis. ML rechaza enlaces y datos de contacto."
        rows={5}
        maxLength={50000 - bloquesFijos.join('\n\n').length - 4}
        style={{
          width: '100%', padding: '10px 12px',
          border: `1px solid ${dirty ? '#3B82F6' : '#CBD5E1'}`,
          borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
          background: dirty ? '#EFF6FF' : '#fff',
          resize: 'vertical', boxSizing: 'border-box', minHeight: 110,
        }}
      />
      <div style={{ marginTop: 6, fontSize: 10.5, color: '#94A3B8', lineHeight: 1.5 }}>
        💡 Texto plano. ML penaliza enlaces externos, teléfonos y referencias a otros sitios.
      </div>

      {/* PARTE FIJA (institucional Limpack) — read-only, leída de app_config */}
      <div style={{ marginTop: 12, padding: 12, background: 'linear-gradient(135deg, rgba(26,52,97,0.04), rgba(0,168,181,0.04))', border: '1px solid rgba(26,52,97,0.15)', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13 }}>🔒</span>
          <strong style={{ fontSize: 11, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>Bloque fijo Limpack · se anexa al final · {bloquesFijos.length} secciones</strong>
        </div>
        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {bloquesFijos.map((b, i) => (
            <p key={i} style={{ margin: i > 0 ? '10px 0 0' : 0 }}>{b}</p>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 10.5, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>ML no acepta emoticones — el panel los limpia automáticamente.</span>
          <Link to="/admin/canales/mercadolibre/descripcion-fija" style={{ color: '#3B82F6', fontWeight: 700, textDecoration: 'none' }}>
            ✏️ Editar bloque fijo →
          </Link>
        </div>
      </div>
    </div>
  )
}

/* ─── Sub-componente: editor de galería 12 fotos
 *
 * Reusa la infraestructura del wizard PublicarProductoML: tabla `ml_fotos` +
 * bucket `productos-fotos-ml`. Mismas funciones (subir/eliminar/portada/mover)
 * que viven directo en BD, sin pasar por el draft del borrador.
 *
 * Cuando el usuario toca algo en la galería, marcamos `sync_fotos=true` en el draft
 * para que al sincronizar se incluyan las fotos en el batch a ML.
 * ──────────────────────────────────────────────────────────── */
function MlGaleriaEditor({ producto, draft, setDraft }) {
  const [fotos, setFotos] = useState([])
  const [placasFijas, setPlacasFijas] = useState([])  // ml_fotos_fijas
  const [urlsPlacas, setUrlsPlacas] = useState({})
  const [subiendo, setSubiendo] = useState(false)
  const [err, setErr] = useState(null)
  const inputRef = useRef(null)

  const MAX_FOTOS = 3   // tope fotos editables del producto
  const PLACAS_FIJAS = 2  // las que se anexan auto al final

  // Cargar fotos del producto desde ml_fotos
  useEffect(() => {
    if (!producto?.id) return
    supabase.from('ml_fotos').select('*')
      .eq('producto_id', producto.id)
      .order('posicion', { ascending: true })
      .then(({ data }) => setFotos(data || []))
  }, [producto?.id])

  // Cargar placas fijas (ml_fotos_fijas) — se anexan al final de toda publicación ML
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ml_fotos_fijas')
        .select('id, slug, titulo, storage_bucket, storage_path, orden')
        .eq('activa', true)
        .order('orden')
        .limit(PLACAS_FIJAS)
      setPlacasFijas(data || [])
      if (data) {
        const u = {}
        for (const p of data) {
          const { data: s } = await supabase.storage.from(p.storage_bucket).createSignedUrl(p.storage_path, 3600)
          if (s?.signedUrl) u[p.slug] = s.signedUrl
        }
        setUrlsPlacas(u)
      }
    })()
  }, [])

  // Marca el draft con sync_fotos para que el botón Sincronizar incluya las fotos
  function marcarDraftFotos() {
    setDraft({ ...draft, sync_fotos: true })
  }

  // ─── Reusa el patrón del wizard PublicarProductoML.subirFoto ───
  async function subirFoto(file) {
    const ext = file.name.split('.').pop()
    const nombreArchivo = `${producto.id}/${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('productos-fotos-ml')
      .upload(nombreArchivo, file, { upsert: false, contentType: file.type })
    if (upErr) throw upErr
    const proxPosicion = fotos.length
    const esPortada = proxPosicion === 0
    const { data: row, error: insErr } = await supabase.from('ml_fotos').insert({
      producto_id: producto.id,
      posicion: proxPosicion,
      tipo: 'base',
      es_portada: esPortada,
      storage_bucket: 'productos-fotos-ml',
      storage_path: nombreArchivo,
      bytes: file.size,
    }).select().single()
    if (insErr) throw insErr
    setFotos(f => [...f, row])
    marcarDraftFotos()
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setSubiendo(true); setErr(null)
    try {
      for (const file of files) {
        if (fotos.length >= MAX_FOTOS) break  // tope hard: 3 fotos editables
        if (!file.type.startsWith('image/')) continue
        await subirFoto(file)
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setSubiendo(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  // ─── Reusa el patrón del wizard PublicarProductoML.eliminarFoto ───
  async function eliminarFoto(foto) {
    if (!confirm('¿Eliminar esta foto?')) return
    await supabase.storage.from('productos-fotos-ml').remove([foto.storage_path])
    await supabase.from('ml_fotos').delete().eq('id', foto.id)
    setFotos(f => f.filter(x => x.id !== foto.id))
    marcarDraftFotos()
  }

  // ─── Reusa PublicarProductoML.moverFoto ───
  async function moverFoto(foto, direccion) {
    const idx = fotos.findIndex(f => f.id === foto.id)
    if (idx < 0) return
    const nuevoIdx = idx + direccion
    if (nuevoIdx < 0 || nuevoIdx >= fotos.length) return
    const ordenadas = [...fotos]
    const tmp = ordenadas[idx]
    ordenadas[idx] = ordenadas[nuevoIdx]
    ordenadas[nuevoIdx] = tmp
    await Promise.all([
      supabase.from('ml_fotos').update({ posicion: nuevoIdx }).eq('id', foto.id),
      supabase.from('ml_fotos').update({ posicion: idx }).eq('id', ordenadas[idx].id),
    ])
    // Recalcular es_portada (la primera en posición 0)
    const primera = ordenadas[0]
    if (primera && !primera.es_portada) {
      await supabase.from('ml_fotos').update({ es_portada: false }).eq('producto_id', producto.id)
      await supabase.from('ml_fotos').update({ es_portada: true }).eq('id', primera.id)
      ordenadas[0] = { ...primera, es_portada: true }
      for (let i = 1; i < ordenadas.length; i++) ordenadas[i] = { ...ordenadas[i], es_portada: false }
    }
    setFotos(ordenadas.map((x, i) => ({ ...x, posicion: i })))
    marcarDraftFotos()
  }

  // ─── Resolver URL pública de la foto vía storage ───
  function urlFoto(f) {
    if (f.ml_picture_url) return f.ml_picture_url
    const { data } = supabase.storage.from(f.storage_bucket || 'productos-fotos-ml').getPublicUrl(f.storage_path)
    return data?.publicUrl
  }

  const dirty = draft.sync_fotos === true

  return (
    <div style={{ marginTop: 14, padding: 14, background: '#F8FAFC', border: `1px solid ${dirty ? '#3B82F6' : '#E2E8F0'}`, borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 12, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          📸 Galería ML · {fotos.length} / {MAX_FOTOS} del producto <span style={{ color: '#94A3B8', fontWeight: 600 }}>+ {placasFijas.length} placas fijas</span>
          {dirty && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#3B82F6', color: '#fff', fontWeight: 700 }}>cambios sin sync</span>}
        </strong>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={subiendo || fotos.length >= MAX_FOTOS}
          style={{
            background: fotos.length >= MAX_FOTOS ? '#CBD5E1' : '#3B82F6', color: '#fff', border: 'none',
            padding: '6px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 700,
            cursor: fotos.length >= MAX_FOTOS || subiendo ? 'not-allowed' : 'pointer',
          }}
        >
          {subiendo ? 'Subiendo…' : fotos.length >= MAX_FOTOS ? `Máximo (${MAX_FOTOS}) alcanzado` : '+ Subir fotos'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleFiles}
          style={{ display: 'none' }}
        />
      </div>
      {/* Grilla: 3 editables del producto + 2 placas fijas (read-only) */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${MAX_FOTOS + PLACAS_FIJAS}, 1fr)`, gap: 8 }}>
        {/* Slots editables del producto */}
        {Array.from({ length: MAX_FOTOS }).map((_, i) => {
          const f = fotos[i]
          const fotoUrl = f ? urlFoto(f) : null
          return (
            <div key={`prod-${i}`} style={{
              position: 'relative', aspectRatio: '1',
              background: fotoUrl ? `url(${fotoUrl}) center/contain no-repeat #fff` : '#F1F5F9',
              border: `1px solid ${i === 0 && f ? '#3B82F6' : '#E2E8F0'}`,
              borderRadius: 6, overflow: 'hidden',
            }}>
              {!f && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{i + 1}</div>
                  <div style={{ fontSize: 9, marginTop: 2, letterSpacing: '.04em', textTransform: 'uppercase' }}>foto producto</div>
                </div>
              )}
              {f && (
                <>
                  {i === 0 && (
                    <div style={{ position: 'absolute', top: 2, left: 2, background: '#3B82F6', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em' }}>
                      PORTADA
                    </div>
                  )}
                  {f.tipo === 'generated' && (
                    <div style={{ position: 'absolute', bottom: 2, left: 2, background: 'rgba(124,58,237,0.85)', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em' }}>
                      IA
                    </div>
                  )}
                  <div style={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 2 }}>
                    {i > 0 && (
                      <button type="button" onClick={() => moverFoto(f, -1)} title="Mover izquierda"
                        style={{ width: 20, height: 20, padding: 0, borderRadius: 3, border: 'none', background: 'rgba(15,31,61,.7)', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>‹</button>
                    )}
                    {i < fotos.length - 1 && (
                      <button type="button" onClick={() => moverFoto(f, 1)} title="Mover derecha"
                        style={{ width: 20, height: 20, padding: 0, borderRadius: 3, border: 'none', background: 'rgba(15,31,61,.7)', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>›</button>
                    )}
                    <button type="button" onClick={() => eliminarFoto(f)} title="Quitar"
                      style={{ width: 20, height: 20, padding: 0, borderRadius: 3, border: 'none', background: 'rgba(220,38,38,.85)', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1, fontWeight: 700 }}>×</button>
                  </div>
                </>
              )}
            </div>
          )
        })}
        {/* Slots placas fijas (4 y 5) — read-only con preview real */}
        {placasFijas.map((p, idx) => {
          const url = urlsPlacas[p.slug]
          const posicion = MAX_FOTOS + idx + 1
          return (
            <div key={p.id} title={p.titulo + ' · placa fija de Limpack — read-only'} style={{
              position: 'relative', aspectRatio: '1',
              background: url
                ? `url(${url}) center/contain no-repeat #F8F6F1`
                : 'linear-gradient(135deg, rgba(26,52,97,0.08), rgba(0,168,181,0.08))',
              border: '2px solid rgba(26,52,97,0.25)',
              borderRadius: 6, overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 2, left: 2, background: 'rgba(26,52,97,0.85)', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em', display: 'flex', alignItems: 'center', gap: 3 }}>
                🔒 FIJA · {posicion}
              </div>
              {!url && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#475569', textAlign: 'center', padding: 6, fontWeight: 600 }}>
                  {p.titulo}
                </div>
              )}
            </div>
          )
        })}
        {/* Si no hay placas cargadas, mostrar slots vacíos con CTA */}
        {placasFijas.length === 0 && Array.from({ length: PLACAS_FIJAS }).map((_, idx) => (
          <div key={`fija-vacio-${idx}`} style={{
            position: 'relative', aspectRatio: '1',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '2px dashed rgba(245, 158, 11, 0.4)',
            borderRadius: 6,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 8, textAlign: 'center',
          }}>
            <div style={{ fontSize: 18, color: '#B45309' }}>🔒</div>
            <div style={{ fontSize: 10, color: '#92400E', fontWeight: 700, marginTop: 4 }}>Sin placa</div>
            <Link to="/admin/canales/mercadolibre/fotos-fijas" style={{ fontSize: 9, color: '#B45309', textDecoration: 'underline', marginTop: 2 }}>Cargar →</Link>
          </div>
        ))}
      </div>
      {err && (
        <div style={{ marginTop: 8, padding: 8, background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 6, fontSize: 11, color: '#7F1D1D' }}>
          ⚠ {err}
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 10.5, color: '#94A3B8', lineHeight: 1.5 }}>
        💡 Subís hasta <strong>{MAX_FOTOS} fotos del producto</strong> (primera = portada). Las <strong>{PLACAS_FIJAS} placas Limpack</strong> en posiciones {MAX_FOTOS + 1}-{MAX_FOTOS + PLACAS_FIJAS} se anexan solas y se editan en <Link to="/admin/canales/mercadolibre/fotos-fijas" style={{ color: '#3B82F6', fontWeight: 700 }}>Fotos fijas ML</Link>. Para generar variantes con IA (fondo blanco / cenital) abrí el <Link to={`/admin/canales/mercadolibre/publicar/${producto.id}`} style={{ color: '#3B82F6', fontWeight: 700 }}>wizard</Link>.
      </div>
    </div>
  )
}

/* ─── Sub-componente: editor de atributos catálogo ─── */
function MlAtributosEditor({ mlPub, draft, setDraft }) {
  const atributosOriginales = Array.isArray(mlPub.attributes) ? mlPub.attributes : []
  const draftAttrs = draft.attributes ?? null
  const atributos = draftAttrs ?? atributosOriginales
  const dirty = draftAttrs !== null

  function setAttrs(nuevos) {
    setDraft({ ...draft, attributes: nuevos })
  }

  function actualizar(idx, valor) {
    const nuevos = atributos.map((a, i) =>
      i === idx ? { ...a, value_name: valor, value: valor } : a
    )
    setAttrs(nuevos)
  }

  if (atributosOriginales.length === 0) {
    return (
      <div style={{ marginTop: 14, padding: 14, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 12, color: '#64748B' }}>
        <strong style={{ fontSize: 12, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
          🏷️ Atributos catálogo
        </strong>
        <p style={{ margin: 0 }}>
          Este producto no tiene atributos cargados todavía. Cargá los obligatorios desde el&nbsp;
          <Link to={`/admin/canales/mercadolibre/publicar/${mlPub.producto_id}`} style={{ color: '#3B82F6', fontWeight: 700 }}>wizard completo</Link>
          &nbsp;(necesita las credenciales ML para listar los disponibles por categoría).
        </p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 14, padding: 14, background: '#F8FAFC', border: `1px solid ${dirty ? '#3B82F6' : '#E2E8F0'}`, borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 12, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          🏷️ Atributos catálogo · {atributos.length}
        </strong>
        <span style={{ fontSize: 11, color: '#94A3B8' }}>
          Los obligatorios están marcados con *
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
        {atributos.map((a, i) => {
          const editado = dirty && atributos[i]?.value_name !== atributosOriginales[i]?.value_name
          const required = a.required || a.tags?.required
          return (
            <div key={a.id || i}>
              <label style={{ display: 'block', fontSize: 10, color: '#1A3461', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                {a.name || a.id} {required && <span style={{ color: '#DC2626' }}>*</span>}
              </label>
              <input
                type="text"
                value={a.value_name ?? a.value ?? ''}
                onChange={e => actualizar(i, e.target.value)}
                placeholder="—"
                style={{
                  width: '100%', padding: '8px 10px',
                  border: `1px solid ${editado ? '#3B82F6' : '#CBD5E1'}`,
                  borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
                  background: editado ? '#EFF6FF' : '#fff',
                  boxSizing: 'border-box',
                }}
              />
              {a.value_id && (
                <div style={{ fontSize: 9.5, color: '#94A3B8', marginTop: 2 }}>
                  ID: <code style={{ fontFamily: 'monospace' }}>{a.value_id}</code>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 10.5, color: '#94A3B8', lineHeight: 1.5 }}>
        💡 Editar texto libre. Para cambiar de "valor preseteado" (value_id), abrí el wizard completo — necesita la lista de opciones de cada atributo desde la API de ML.
      </div>
    </div>
  )
}

/* ─── Sub-componente: simulador de envío por canal y barrio
 *
 * Reusa src/lib/envios.js (mismas tarifas y lógica que la tienda) y
 * src/lib/portalClienteApi.js → getBarriosActivos.
 *
 * Muestra para el peso facturable del producto:
 *   - Costo Limpack propio (en ruta vs fuera de ruta) por barrio
 *   - Costo Mercado Envíos estimado (tabla peso-tarifa UY 2026)
 *   - Reparto manual (DAC etc) — input editable
 * ──────────────────────────────────────────────────────────── */
function SimuladorEnvio({ producto }) {
  const [barrios, setBarrios] = useState([])
  const [tarifas, setTarifas] = useState(null)
  const [tarifaMLTabla, setTarifaMLTabla] = useState([])  // de app_config.ml_tarifa_envios_estimada
  const [dacManual, setDacManual] = useState(180)

  useEffect(() => {
    // Cargar barrios + tarifas + tabla ML en paralelo
    Promise.all([
      supabase.from('barrios').select('id, nombre, dias').eq('activo', true).order('nombre').then(r => r.data || []),
      supabase.from('envio_tarifas').select('clave, monto').then(r => r.data || []),
      supabase.from('app_config').select('value').eq('key', 'ml_tarifa_envios_estimada').maybeSingle().then(r => r.data),
    ]).then(([bs, ts, mlT]) => {
      setBarrios(bs)
      const t = {}
      for (const r of ts) t[r.clave] = Number(r.monto)
      setTarifas(t)
      if (mlT?.value) {
        const v = typeof mlT.value === 'string' ? JSON.parse(mlT.value) : mlT.value
        if (Array.isArray(v)) setTarifaMLTabla([...v].sort((a, b) => Number(a.hasta_kg) - Number(b.hasta_kg)))
      }
    })
  }, [])

  // Peso facturable: el mayor entre peso real y peso volumétrico (L*A*H/6000)
  const pesoRealKg = Number(producto.peso_g || 0) / 1000
  const volumenL = (Number(producto.largo_cm) * Number(producto.ancho_cm) * Number(producto.alto_cm)) / 1000
  const pesoVolKg = volumenL / 6
  const pesoFactKg = Math.max(pesoRealKg, pesoVolKg)
  const cobraPor = pesoFactKg === pesoRealKg ? 'real' : 'volumétrico'

  // Tarifa Mercado Envíos UY estimada por peso facturable — leída de app_config
  function tarifaML(kg) {
    if (tarifaMLTabla.length === 0) return 0
    const rango = tarifaMLTabla.find(r => kg <= Number(r.hasta_kg))
    return rango ? Number(rango.costo) : Number(tarifaMLTabla[tarifaMLTabla.length - 1].costo)
  }
  const costoML = tarifaML(pesoFactKg)

  if (!tarifas) {
    return (
      <div style={{ marginTop: 14, padding: 14, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 12, color: '#94A3B8' }}>
        Cargando tarifas…
      </div>
    )
  }

  // Limpack propio: tarifa fuera de ruta de tienda chica (caso más común: producto suelto)
  const limpackEnRuta = 0
  const limpackFueraRuta = tarifas.no_susc_tienda_chica_fuera_ruta ?? 250

  return (
    <div style={{ marginTop: 14, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 13, color: '#1A3461' }}>🚚 Simulador de envío</strong>
        <span style={{ fontSize: 11, color: '#64748B' }}>
          Peso facturable: <strong style={{ color: cobraPor === 'volumétrico' ? '#B45309' : '#10B981' }}>{pesoFactKg.toFixed(2)} kg</strong> ({cobraPor})
        </span>
      </div>

      {/* Resumen costo por canal */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
        <div style={{ padding: 12, background: 'rgba(16,185,129,0.06)', borderRadius: 8, border: '1px solid rgba(16,185,129,0.2)' }}>
          <div style={{ fontSize: 10, color: '#065F46', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800 }}>💧 Limpack propio</div>
          <div style={{ fontSize: 18, color: '#1A3461', fontWeight: 800, marginTop: 2 }}>
            $0 <span style={{ fontSize: 11, fontWeight: 500, color: '#64748B' }}>en ruta</span>
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
            ${limpackFueraRuta} fuera de ruta · gratis si carrito ≥ ${tarifas.umbral_tienda_envio_gratis ?? 1000}
          </div>
        </div>
        <div style={{ padding: 12, background: 'rgba(59,130,246,0.06)', borderRadius: 8, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 10, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800 }}>🟡 Mercado Envíos (est.)</div>
          <div style={{ fontSize: 18, color: '#1A3461', fontWeight: 800, marginTop: 2 }}>${costoML}</div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
            tabla {pesoFactKg.toFixed(2)} kg · UY 2026
          </div>
        </div>
        <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, border: '1px solid #CBD5E1' }}>
          <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800 }}>📦 Reparto manual (DAC)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <span style={{ fontSize: 13, color: '#1A3461' }}>$</span>
            <input
              type="number"
              value={dacManual}
              onChange={e => setDacManual(Number(e.target.value) || 0)}
              style={{ width: 70, padding: '4px 8px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14, fontWeight: 700, color: '#1A3461' }}
            />
            <span style={{ fontSize: 11, color: '#64748B' }}>editable</span>
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
            Cuando no llega Limpack/ML
          </div>
        </div>
      </div>

      {/* Tabla por barrio */}
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#1A3461', padding: '6px 0' }}>
          Costo por barrio ({barrios.length} cubiertos)
        </summary>
        <div style={{ marginTop: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Barrio</th>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Días Limpack</th>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right' }}>Limpack ruta</th>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right' }}>Limpack fuera</th>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right' }}>ML est.</th>
                <th style={{ padding: '6px 8px', fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right' }}>DAC</th>
              </tr>
            </thead>
            <tbody>
              {barrios.map(b => (
                <tr key={b.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '7px 8px', fontWeight: 700, color: '#1A3461' }}>{b.nombre}</td>
                  <td style={{ padding: '7px 8px', color: '#64748B', fontSize: 11 }}>{Array.isArray(b.dias) ? b.dias.join(', ') : '—'}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#10B981', fontWeight: 700 }}>$0</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#1A3461' }}>${limpackFueraRuta}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#1A3461' }}>${costoML}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', color: '#475569' }}>${dacManual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <div style={{ marginTop: 10, fontSize: 10.5, color: '#475569', lineHeight: 1.5, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span>💡 Limpack: tarifas reales de <code>envio_tarifas</code>. ML: tabla estimada — reemplazar por API real al conectar la cuenta. DAC: editable.</span>
        <span style={{ display: 'flex', gap: 12 }}>
          <Link to="/admin/logistica/tarifas-envio" style={{ color: '#10B981', fontWeight: 700, textDecoration: 'none' }}>✏️ Editar tarifas Limpack</Link>
          <Link to="/admin/canales/mercadolibre/tarifas" style={{ color: '#3B82F6', fontWeight: 700, textDecoration: 'none' }}>✏️ Editar tabla ML</Link>
        </span>
      </div>
    </div>
  )
}

/* ─── Sub-componente: menú ⋯ de acciones para fila de kit ─── */
function KitFilaAcciones({ onEditar, onQuitar }) {
  const [abierto, setAbierto] = useState(false)
  const refMenu = useRef(null)

  // Click fuera cierra
  useEffect(() => {
    if (!abierto) return
    function handleClick(e) {
      if (refMenu.current && !refMenu.current.contains(e.target)) setAbierto(false)
    }
    function handleEsc(e) { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [abierto])

  return (
    <div ref={refMenu} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        title="Acciones"
        aria-label="Acciones"
        style={{
          background: abierto ? '#1A3461' : 'transparent',
          color: abierto ? '#fff' : '#475569',
          border: '1px solid #CBD5E1',
          borderRadius: 6,
          padding: '4px 10px',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          fontWeight: 700,
        }}
      >⋯</button>
      {abierto && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 100,
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            boxShadow: '0 8px 20px rgba(15,31,61,0.15)',
            minWidth: 140,
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => { setAbierto(false); onEditar() }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'transparent', border: 'none',
              cursor: 'pointer', fontSize: 13, color: '#1A3461',
              fontFamily: 'inherit',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >✏️ Editar cantidad</button>
          <button
            type="button"
            onClick={() => { setAbierto(false); onQuitar() }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'transparent', border: 'none',
              borderTop: '1px solid #F1F5F9',
              cursor: 'pointer', fontSize: 13, color: '#7F1D1D',
              fontFamily: 'inherit',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >🗑️ Quitar del kit</button>
        </div>
      )}
    </div>
  )
}
